

import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useLoader } from "../../../../context/LoaderProvider.jsx";

const FoodSuperadminCampaign = () => {
    const [campaigns, setCampaigns] = useState([]);
    const [formData, setFormData] = useState({
        campaignName: "",
        description: "",
        interests: ["JOBS"],
        goal: "BRANDING",
        medium: "APP",
        status: "ACTIVE",
        startDate: "",
        subGoal: "",
        endDate: "",
        city: "",
        radiusKm: 100,
        centerLatitude: 17.3850,
        centerLongitude: 78.4867,
        deepLink: "",
        appType: "FOOD_AND_BEVERAGES",
        mediaType: "IMAGE",
        addDisplayPosition: "ADD_SCREEN",
        customerId: "ADM161220251",
        paymentStatus: "PENDING",
        loops: 7,
        period: "PER_DAY",
        targetAudience: ["Users"], // ✅ ADD THIS
        targeting: {
            interests: ["JOBS"],
            city: "",
            centerLatitude: 17.3850,
            centerLongitude: 78.4867,
            radiusKm: 100,
            targetAudience: ["Users"],
        }
    });
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);
    const [viewCampaign, setViewCampaign] = useState(null);
    const [creativeFile, setCreativeFile] = useState(null);
    const { showLoader, hideLoader } = useLoader();
    const [loading, setLoading] = useState(true);
    const [locationLoaded, setLocationLoaded] = useState(false);
    const isMobile = window.innerWidth <= 768;

    // Digital-specific states
    const [screenIds, setScreenIds] = useState([]);
    const [screens, setScreens] = useState([]);
    const [loadingScreens, setLoadingScreens] = useState(false);
    const [creativeResolution, setCreativeResolution] = useState(null);
    const [creativeDuration, setCreativeDuration] = useState(null);
    const [creativeValidationMessages, setCreativeValidationMessages] = useState([]);

    // New state for rejection input inside view modal
    const [showRejectionInput, setShowRejectionInput] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    // New state for approval modal
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [selectedCampaignForApproval, setSelectedCampaignForApproval] = useState(null);
    const [approvalData, setApprovalData] = useState({
        campaignId: null,
        approvalStatus: "PENDING",
        rejectionReason: ""
    });

    // New state for media preview outside popup
    const [showMediaPreview, setShowMediaPreview] = useState(false);
    const [previewMediaUrl, setPreviewMediaUrl] = useState('');
    const [previewMediaType, setPreviewMediaType] = useState('');

    // Refs
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const circleRef = useRef(null);
    const searchInputRef = useRef(null);

    // Interest options (matches enum)
    const interestOptions = [
        "JOBS",
        "FOOD",
        "EDUCATION",
        "OFFERS",
        "REAL_ESTATE",
        "ONLINE_COURSES",
        "BAKERY",
        "HEALTH",
        "TRAVEL",
        "ENTERTAINMENT"
    ];

    // Goal options
    const goalOptions = [
        "BRANDING",
        "DISCOUNT",
        "LEADS",
        "EVENTS",
        "SPONSORSHIP"
    ];
    const subGoalOptionsMap = {
        BRANDING: [
            "BRAND_AWARENESS",
            "BRAND_RECALL",
            "PREMIUM_POSTING"
        ],
        LEADS: [
            "GET_MORE_MESSAGES",
            "GET_MORE_CALLS",
            "GET_MORE_WHATSAPP_MESSAGE",
            "GET_MORE_PAGE_LIKES",
            "GET_MORE_LEADS",
            "GET_MORE_WEBSITE_VISITORS"
        ]
    };
    const subGoalOptions = subGoalOptionsMap[formData.goal] || [];
    // Medium options
    const mediumOptions = [
        "APP",
        "DIGITAL",
        "PHYSICAL"
    ];

    // Status options
    const statusOptions = [
        "DRAFT",
        "SCHEDULED",
        "ACTIVE",
        "PAUSED",
        "COMPLETED",
        "CANCELLED"
    ];

    // Approval Status options
    const approvalStatusOptions = [
        "PENDING",
        "APPROVED",
        "REJECTED"
    ];

    // Payment Status options
    const paymentStatusOptions = [
        "PENDING",
        "PAID",
        "FAILED",
        "REFUNDED",
        "CANCELLED"
    ];

    // App Type options
    const appTypeOptions = [
        "FOOD_AND_BEVERAGES",
        "RETAIL",
        "SERVICES",
        "ENTERTAINMENT",
        "EDUCATION",
        "HEALTHCARE"
    ];

    // Add Display Position options
    const displayPositionOptions = [
        "ADD_SCREEN",
        "HOMEPAGE_BANNER",
        "PRODUCT_PAGE",
        "CHECKOUT_PAGE",
        "IN_APP_POPUP"
    ];

    // Media type options
    const mediaTypeOptions = [
        "IMAGE",
        "VIDEO"
    ];

    // Display Position Best Ratio mapping
    const displayPositionRatios = {
        "ADD_SCREEN": { ratio: "9:16", resolution: "R_720P", description: "Full-screen vertical ad" },
        "HOMEPAGE_BANNER": { ratio: "16:9", resolution: "R_4K", description: "Wide banner style" },
        "PRODUCT_PAGE": { ratio: "4:5", resolution: "R_1080P", description: "Product-focused layout" },
        "CHECKOUT_PAGE": { ratio: "4:5", resolution: "R_1080P", description: "Compact promotional block" },
        "IN_APP_POPUP": { ratio: "9:16", resolution: "R_720P", description: "Overlay popup style" }
    };

    // Creative requirements based on display position
    const getCreativeRequirements = (position) => {
        const requirements = {
            "ADD_SCREEN": {
                formats: ["JPG", "PNG", "MP4"],
                dimensions: "1920x1080 pixels",
                maxSize: "6 MB for images, 10 MB for videos",
                aspectRatio: "16:9",
                duration: "15-30 seconds for videos",
                bestRatio: "9:16 (R_720P)"
            },
            "HOMEPAGE_BANNER": {
                formats: ["JPG", "PNG", "GIF"],
                dimensions: "1200x400 pixels",
                maxSize: "2 MB for images, 5 MB for GIFs",
                aspectRatio: "3:1",
                duration: "N/A",
                bestRatio: "16:9 (R_4K)"
            },
            "PRODUCT_PAGE": {
                formats: ["JPG", "PNG"],
                dimensions: "800x800 pixels",
                maxSize: "1 MB",
                aspectRatio: "1:1",
                duration: "N/A",
                bestRatio: "4:5 (R_1080P)"
            },
            "CHECKOUT_PAGE": {
                formats: ["JPG", "PNG"],
                dimensions: "600x200 pixels",
                maxSize: "500 KB",
                aspectRatio: "3:1",
                duration: "N/A",
                bestRatio: "4:5 (R_1080P)"
            },
            "IN_APP_POPUP": {
                formats: ["JPG", "PNG", "MP4"],
                dimensions: "800x600 pixels",
                maxSize: "2 MB for images, 8 MB for videos",
                aspectRatio: "4:3",
                duration: "10-15 seconds for videos",
                bestRatio: "9:16 (R_720P)"
            }
        };
        return requirements[position] || requirements["ADD_SCREEN"];
    };

    // Function to compress image
    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    // Calculate new dimensions (maintain aspect ratio)
                    let width = img.width;
                    let height = img.height;

                    // Reduce quality by 30%
                    const quality = 0.7;

                    canvas.width = width;
                    canvas.height = height;

                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        const compressedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now()
                        });
                        resolve(compressedFile);
                    }, file.type, quality);
                };
                img.onerror = (error) => reject(error);
            };
            reader.onerror = (error) => reject(error);
        });
    };

    // Function to compress video
    const compressVideo = (file) => {
        return new Promise((resolve, reject) => {
            console.log("Video compression would happen here - returning original for now");
            resolve(file);
        });
    };

    // Load Google Maps script once
    useEffect(() => {
        if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCf7bYn7iDIs2T6-sDnmr7qWy9oFZOPOuc&libraries=places`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                console.log("Google Maps loaded successfully");
            };
            document.head.appendChild(script);
        }
    }, []);

    // Fetch all campaigns
    useEffect(() => {
        const fetchCampaigns = async () => {
            try {
                const token = localStorage.getItem("authToken");
                showLoader();
                setLoading(true);

                const response = await axios.get(
                    `https://backend.maamaas.com/promotions/api/admin/all/campaigns`,
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );

                const campaignsArray = Array.isArray(response.data) ? response.data : [response.data];
                setCampaigns(campaignsArray);

            } catch (error) {
                console.error("❌ Error fetching campaigns:", error);
                alert("Failed to fetch campaigns");
            } finally {
                setLoading(false);
                hideLoader();
            }
        };

        fetchCampaigns();
    }, []);

    // Fetch screens when modal opens for DIGITAL medium
    useEffect(() => {
        if ((showAddModal || showEditModal) && formData.medium === "DIGITAL") {
            fetchScreens();
        }
    }, [showAddModal, showEditModal, formData.medium]);

    // Fetch screens from API
    const fetchScreens = async () => {
        try {
            setLoadingScreens(true);
            const token = localStorage.getItem("authToken");

            const response = await axios.get(
                `https://backend.maamaas.com/promotions/api/user/screens`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            console.log("Screens response:", response.data);

            // Handle different response formats
            let screensData = [];
            if (Array.isArray(response.data)) {
                screensData = response.data;
            } else if (response.data.data && Array.isArray(response.data.data)) {
                screensData = response.data.data;
            } else if (response.data.screens && Array.isArray(response.data.screens)) {
                screensData = response.data.screens;
            } else if (response.data.content && Array.isArray(response.data.content)) {
                screensData = response.data.content;
            }

            setScreens(screensData);

            if (screensData.length === 0) {
                console.warn("No screens found in response");
            }
        } catch (error) {
            console.error("❌ Error fetching screens:", error);
            alert("Failed to fetch screens. Please try again.");
            setScreens([]);
        } finally {
            setLoadingScreens(false);
        }
    };

    // Initialize map when modal opens
    useEffect(() => {
        if ((showAddModal || showEditModal) && window.google) {
            setTimeout(() => {
                initMap();
            }, 300);
        } else {
            if (mapInstanceRef.current) {
                mapInstanceRef.current = null;
                markerRef.current = null;
                circleRef.current = null;
            }
        }
    }, [showAddModal, showEditModal]);

    // Get current location
    const getCurrentLocation = () => {
        if (navigator.geolocation) {
            setLocationLoaded(false);
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setFormData(prev => ({
                        ...prev,
                        centerLatitude: latitude,
                        centerLongitude: longitude,
                        targeting: {
                            ...prev.targeting,
                            centerLatitude: latitude,
                            centerLongitude: longitude
                        }
                    }));

                    if (mapInstanceRef.current) {
                        const newPosition = { lat: latitude, lng: longitude };
                        mapInstanceRef.current.setCenter(newPosition);
                        mapInstanceRef.current.setZoom(13);
                        if (markerRef.current) {
                            markerRef.current.setPosition(newPosition);
                        }
                        updateCircle();
                    }
                    setLocationLoaded(true);
                },
                (error) => {
                    console.error("Error getting location:", error);
                    alert("Please enable location services");
                    setLocationLoaded(true);
                }
            );
        } else {
            alert("Geolocation is not supported by this browser");
            setLocationLoaded(true);
        }
    };

    // Go to city
    const goToCity = (cityName) => {
        if (!cityName || !window.google) {
            alert("Please enter a city name");
            return;
        }

        setLocationLoaded(false);

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: cityName }, (results, status) => {
            if (status === 'OK' && results[0]) {
                const location = results[0].geometry.location;
                const lat = location.lat();
                const lng = location.lng();

                let cityNameFromResult = cityName;
                for (const component of results[0].address_components) {
                    if (component.types.includes('locality') ||
                        component.types.includes('administrative_area_level_2') ||
                        component.types.includes('administrative_area_level_1')) {
                        cityNameFromResult = component.long_name;
                        break;
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    centerLatitude: lat,
                    centerLongitude: lng,
                    city: cityNameFromResult,
                    targeting: {
                        ...prev.targeting,
                        centerLatitude: lat,
                        centerLongitude: lng,
                        city: cityNameFromResult
                    }
                }));

                if (mapInstanceRef.current) {
                    const newPosition = { lat, lng };
                    mapInstanceRef.current.setCenter(newPosition);
                    mapInstanceRef.current.setZoom(13);
                    if (markerRef.current) {
                        markerRef.current.setPosition(newPosition);
                    }
                    updateCircle();
                }
                setLocationLoaded(true);
            } else {
                alert(`City "${cityName}" not found`);
                setLocationLoaded(true);
            }
        });
    };

    // Update circle based on radius
    const updateCircle = () => {
        if (!mapInstanceRef.current || !formData.centerLatitude || !formData.centerLongitude) return;

        if (circleRef.current) {
            circleRef.current.setMap(null);
            circleRef.current = null;
        }

        if (formData.radiusKm > 0) {
            const circle = new window.google.maps.Circle({
                strokeColor: '#FF5722',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: '#FF5722',
                fillOpacity: 0.2,
                map: mapInstanceRef.current,
                center: { lat: formData.centerLatitude, lng: formData.centerLongitude },
                radius: formData.radiusKm * 1000
            });
            circleRef.current = circle;
        }
    };

    // Initialize map
    const initMap = () => {
        const mapId = showAddModal ? 'add-map' : 'edit-map';
        const mapElement = document.getElementById(mapId);

        if (!mapElement || !window.google) {
            console.log("Map element or Google not ready");
            return;
        }

        console.log("Initializing map for", mapId);

        const defaultLocation = {
            lat: formData.centerLatitude || 17.3850,
            lng: formData.centerLongitude || 78.4867
        };

        const map = new window.google.maps.Map(mapElement, {
            center: defaultLocation,
            zoom: 13,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
            zoomControl: true
        });

        const marker = new window.google.maps.Marker({
            position: defaultLocation,
            map: map,
            draggable: true,
            animation: window.google.maps.Animation.DROP,
            title: "Drag to set location"
        });

        marker.addListener('dragend', () => {
            const position = marker.getPosition();
            setFormData(prev => ({
                ...prev,
                centerLatitude: position.lat(),
                centerLongitude: position.lng(),
                targeting: {
                    ...prev.targeting,
                    centerLatitude: position.lat(),
                    centerLongitude: position.lng()
                }
            }));
            updateCircle();
        });

        map.addListener('click', (e) => {
            marker.setPosition(e.latLng);
            setFormData(prev => ({
                ...prev,
                centerLatitude: e.latLng.lat(),
                centerLongitude: e.latLng.lng(),
                targeting: {
                    ...prev.targeting,
                    centerLatitude: e.latLng.lat(),
                    centerLongitude: e.latLng.lng()
                }
            }));
            updateCircle();
        });

        if (searchInputRef.current) {
            const searchBox = new window.google.maps.places.SearchBox(searchInputRef.current);

            map.addListener('bounds_changed', () => {
                searchBox.setBounds(map.getBounds());
            });

            searchBox.addListener('places_changed', () => {
                const places = searchBox.getPlaces();
                if (places.length === 0) return;

                const place = places[0];
                if (!place.geometry) return;

                if (place.geometry.viewport) {
                    map.fitBounds(place.geometry.viewport);
                } else {
                    map.setCenter(place.geometry.location);
                    map.setZoom(15);
                }

                marker.setPosition(place.geometry.location);

                let cityName = formData.city;
                if (place.address_components) {
                    for (const component of place.address_components) {
                        if (component.types.includes('locality') ||
                            component.types.includes('administrative_area_level_2')) {
                            cityName = component.long_name;
                            break;
                        }
                    }
                }

                setFormData(prev => ({
                    ...prev,
                    centerLatitude: place.geometry.location.lat(),
                    centerLongitude: place.geometry.location.lng(),
                    city: cityName,
                    targeting: {
                        ...prev.targeting,
                        centerLatitude: place.geometry.location.lat(),
                        centerLongitude: place.geometry.location.lng(),
                        city: cityName
                    }
                }));
                updateCircle();
            });
        }

        mapInstanceRef.current = map;
        markerRef.current = marker;
        setLocationLoaded(true);

        setTimeout(() => updateCircle(), 500);
    };

    // Handle form input changes
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "centerLatitude" || name === "centerLongitude") {
            const numValue = parseFloat(value) || 0;
            setFormData(prev => ({
                ...prev,
                [name]: numValue,
                targeting: {
                    ...prev.targeting,
                    [name]: numValue
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value,
                targeting: {
                    ...prev.targeting,
                    [name]: value
                }
            }));
        }

        if (name === 'radiusKm' && mapInstanceRef.current) {
            setTimeout(() => updateCircle(), 100);
        }
    };

    // Handle screen selection with checkboxes
    const handleScreenSelection = (screenId) => {
        setScreenIds(prev => {
            if (prev.includes(screenId)) {
                return prev.filter(id => id !== screenId);
            } else {
                return [...prev, screenId];
            }
        });
    };

    // Check if creative is valid for screens
    const isCreativeValidForScreens = () => {
        if (!creativeFile) return false;

        const fileExt = creativeFile.name.split('.').pop().toUpperCase();
        const videoExtensions = ['MP4', 'MOV', 'AVI', 'MKV', 'WEBM', 'OGG'];
        const isVideo = videoExtensions.includes(fileExt);

        // For videos, check duration (max 20 seconds)
        if (isVideo && creativeDuration && creativeDuration > 20) {
            return false;
        }

        return true;
    };

    // Get creative resolution string
    const getCreativeResolution = () => {
        return "16:9 (1920x1080)";
    };

    // Check if a screen is compatible with the uploaded creative
    const isScreenCompatible = (screen) => {
        if (!creativeFile) return false;

        const fileExt = creativeFile.name.split('.').pop().toUpperCase();
        const videoExtensions = ['MP4', 'MOV', 'AVI', 'MKV', 'WEBM', 'OGG'];
        const isVideo = videoExtensions.includes(fileExt);

        // Check video duration
        if (isVideo && creativeDuration && creativeDuration > 20) {
            return false;
        }

        // Digital screens typically require 16:9 aspect ratio
        const screenResolution = screen.resolution || screen.aspectRatio || '';
        const screenRatio = screenResolution.toLowerCase();

        // Accept screens with 16:9 resolution or standard digital display resolutions
        const compatibleResolutions = ['16:9', '1920x1080', '1280x720', '3840x2160'];
        const isCompatible = compatibleResolutions.some(res => screenRatio.includes(res.toLowerCase()));

        return isCompatible;
    };

    // Get compatible screens
    const getCompatibleScreens = () => {
        if (!creativeFile) return [];
        return screens.filter(screen => isScreenCompatible(screen));
    };

    // Handle select all compatible screens
    const handleSelectAllCompatibleScreens = () => {
        const compatibleScreens = getCompatibleScreens().map(screen => screen.id || screen.screenId);

        if (screenIds.length === compatibleScreens.length) {
            setScreenIds([]);
        } else {
            setScreenIds(compatibleScreens);
        }
    };

    // Handle creative file selection with compression and validation
    const handleCreativeChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            showLoader();

            const requirements = getCreativeRequirements(formData.addDisplayPosition);
            const fileExt = file.name.split('.').pop().toUpperCase();
            const fileSizeInMB = file.size / (1024 * 1024);
            const videoExtensions = ['MP4', 'MOV', 'AVI', 'MKV', 'WEBM', 'OGG'];
            const isVideo = videoExtensions.includes(fileExt);

            if (!requirements.formats.includes(fileExt)) {
                alert(`Invalid file format. Please upload: ${requirements.formats.join(', ')}`);
                e.target.value = '';
                hideLoader();
                return;
            }

            const detectedMediaType = isVideo ? 'VIDEO' : 'IMAGE';

            if (detectedMediaType !== formData.mediaType) {
                alert(`File type mismatch. You selected ${formData.mediaType} but uploaded a ${detectedMediaType} file.`);
                e.target.value = '';
                hideLoader();
                return;
            }

            const maxSize = isVideo ?
                parseFloat(requirements.maxSize.split(',')[1]?.split('MB')[0] || '10') :
                parseFloat(requirements.maxSize.split('MB')[0]);

            if (fileSizeInMB > maxSize) {
                alert(`File size too large. Maximum size: ${requirements.maxSize}`);
                e.target.value = '';
                hideLoader();
                return;
            }

            // For videos, check duration
            if (isVideo) {
                const video = document.createElement('video');
                video.preload = 'metadata';

                const duration = await new Promise((resolve) => {
                    video.onloadedmetadata = () => {
                        resolve(video.duration);
                    };
                    video.src = URL.createObjectURL(file);
                });

                setCreativeDuration(duration);

                if (duration > 20) {
                    alert(`Video duration too long. Maximum duration is 20 seconds. Your video is ${Math.round(duration)} seconds.`);
                    e.target.value = '';
                    URL.revokeObjectURL(video.src);
                    hideLoader();
                    return;
                }

                URL.revokeObjectURL(video.src);
            } else {
                setCreativeDuration(null);
            }

            // Set creative resolution info
            setCreativeResolution("16:9 (1920x1080)");

            // Build validation messages
            const messages = [];
            messages.push({
                message: `Format: ${fileExt}`,
                valid: true
            });
            messages.push({
                message: `Size: ${fileSizeInMB.toFixed(2)}MB / ${maxSize}MB`,
                valid: fileSizeInMB <= maxSize
            });
            if (isVideo) {
                messages.push({
                    message: `Duration: ${creativeDuration?.toFixed(1) || '?'}s / 20s`,
                    valid: creativeDuration <= 20
                });
            }
            setCreativeValidationMessages(messages);

            let compressedFile = file;
            if (isVideo) {
                compressedFile = await compressVideo(file);
                console.log(`Video compressed by 30%: ${(file.size / (1024 * 1024)).toFixed(2)}MB → ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`);
            } else {
                compressedFile = await compressImage(file);
                console.log(`Image compressed by 30%: ${(file.size / (1024 * 1024)).toFixed(2)}MB → ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`);
            }

            setCreativeFile(compressedFile);

            // Reset screen selection when new creative is uploaded
            setScreenIds([]);

            alert(`File processed successfully! Original: ${fileSizeInMB.toFixed(2)}MB, Compressed: ${(compressedFile.size / (1024 * 1024)).toFixed(2)}MB`);

        } catch (error) {
            console.error("Error processing file:", error);
            alert("Error processing file. Please try again.");
        } finally {
            hideLoader();
        }
    };

    // Handle approval input changes
    const handleApprovalChange = (e) => {
        const { name, value } = e.target;
        setApprovalData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Fetch campaign by ID
    const fetchCampaignById = async (campaignId) => {
        try {
            const token = localStorage.getItem("authToken");
            showLoader();

            const response = await axios.get(
                `https://backend.maamaas.com/promotions/api/user/campaign/${campaignId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            console.log("Campaign details:", response.data);
            return response.data;

        } catch (error) {
            console.error("❌ Error fetching campaign:", error);
            alert("Failed to fetch campaign details");
            return null;
        } finally {
            hideLoader();
        }
    };

    // Update campaign approval status
    const handleApprovalUpdate = async (e) => {
        e.preventDefault();

        if (!selectedCampaignForApproval) {
            alert("No campaign selected for approval");
            return;
        }

        try {
            const token = localStorage.getItem("authToken");
            showLoader();

            const campaignId = selectedCampaignForApproval.id || selectedCampaignForApproval.campaignId;

            const approvalPayload = {
                campaignId: campaignId,
                approvalStatus: approvalData.approvalStatus,
                rejectionReason: approvalData.rejectionReason || ""
            };

            console.log("Submitting approval:", approvalPayload);

            const response = await axios.put(
                `https://backend.maamaas.com/promotions/api/admin/campaign/approval`,
                approvalPayload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log("Approval response:", response.data);

            const updatedCampaignId = response.data.campaignId || campaignId;

            setCampaigns(prev =>
                prev.map(campaign => {
                    if (campaign.id === updatedCampaignId || campaign.campaignId === updatedCampaignId || campaign.id === campaignId) {
                        console.log("Updating campaign approval:", campaign.id, "to", approvalData.approvalStatus);
                        return {
                            ...campaign,
                            approvalStatus: approvalData.approvalStatus
                        };
                    }
                    return campaign;
                })
            );

            if (viewCampaign && (viewCampaign.id === updatedCampaignId || viewCampaign.id === campaignId)) {
                setViewCampaign(prev => ({
                    ...prev,
                    approvalStatus: approvalData.approvalStatus
                }));
            }

            alert(`Campaign ${approvalData.approvalStatus.toLowerCase()} successfully!`);

            setShowApprovalModal(false);
            setSelectedCampaignForApproval(null);
            setApprovalData({
                campaignId: null,
                approvalStatus: "PENDING",
                rejectionReason: ""
            });

        } catch (error) {
            console.error("❌ Error updating campaign approval:", error.response?.data || error.message);
            alert(`Failed to update campaign approval: ${error.response?.data?.message || error.message}`);
        } finally {
            hideLoader();
        }
    };

    // Direct Approve function (from view modal)
    const handleDirectApprove = async (campaign) => {
        if (!window.confirm(`Are you sure you want to approve campaign "${campaign.campaignName}"?`)) {
            return;
        }

        try {
            const token = localStorage.getItem("authToken");
            showLoader();

            console.log("Campaign to approve:", campaign);

            const campaignId = campaign.id || campaign.campaignId;

            if (!campaignId) {
                throw new Error("Campaign ID not found");
            }

            const approvalPayload = {
                campaignId: campaignId,
                approvalStatus: "APPROVED",
                rejectionReason: ""
            };

            console.log("Sending approval payload:", approvalPayload);

            const response = await axios.put(
                `https://backend.maamaas.com/promotions/api/admin/campaign/approval`,
                approvalPayload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log("Approval response:", response.data);

            if (response.status === 200 || response.status === 201 || response.status === 204) {
                const updatedCampaignId = response.data.campaignId || campaignId;

                setCampaigns(prev =>
                    prev.map(c => {
                        if (c.id === updatedCampaignId || c.campaignId === updatedCampaignId || c.id === campaignId) {
                            console.log("Updating campaign:", c.id, "to APPROVED");
                            return {
                                ...c,
                                approvalStatus: "APPROVED"
                            };
                        }
                        return c;
                    })
                );

                if (viewCampaign && (viewCampaign.id === updatedCampaignId || viewCampaign.id === campaignId)) {
                    setViewCampaign(prev => ({
                        ...prev,
                        approvalStatus: "APPROVED"
                    }));
                }

                alert("Campaign approved successfully!");
            } else {
                throw new Error("Unexpected response status");
            }

        } catch (error) {
            console.error("❌ Error approving campaign:", error);
            console.error("Error details:", error.response?.data || error.message);

            const errorMessage = error.response?.data?.message || error.message || "Failed to approve campaign";
            alert(`Failed to approve campaign: ${errorMessage}`);
        } finally {
            hideLoader();
        }
    };

    // Direct Reject function
    const handleDirectReject = async (campaign) => {
        try {
            const token = localStorage.getItem("authToken");
            showLoader();

            console.log("Campaign to reject:", campaign);

            const campaignId = campaign.id || campaign.campaignId;

            if (!campaignId) {
                throw new Error("Campaign ID not found");
            }

            const approvalPayload = {
                campaignId: campaignId,
                approvalStatus: "REJECTED",
                rejectionReason: rejectionReason.trim()
            };

            console.log("Sending rejection payload:", approvalPayload);

            const response = await axios.put(
                `https://backend.maamaas.com/promotions/api/admin/campaign/approval`,
                approvalPayload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );

            console.log("Rejection response:", response.data);

            if (response.status === 200 || response.status === 201 || response.status === 204) {
                const updatedCampaignId = response.data.campaignId || campaignId;

                setCampaigns(prev =>
                    prev.map(c => {
                        if (c.id === updatedCampaignId || c.campaignId === updatedCampaignId || c.id === campaignId) {
                            console.log("Updating campaign:", c.id, "to REJECTED");
                            return {
                                ...c,
                                approvalStatus: "REJECTED"
                            };
                        }
                        return c;
                    })
                );

                if (viewCampaign && (viewCampaign.id === updatedCampaignId || viewCampaign.id === campaignId)) {
                    setViewCampaign(prev => ({
                        ...prev,
                        approvalStatus: "REJECTED"
                    }));
                }

                alert("Campaign rejected successfully!");

                setShowRejectionInput(false);
                setRejectionReason('');
            } else {
                throw new Error("Unexpected response status");
            }

        } catch (error) {
            console.error("❌ Error rejecting campaign:", error);
            console.error("Error details:", error.response?.data || error.message);

            const errorMessage = error.response?.data?.message || error.message || "Failed to reject campaign";
            alert(`Failed to reject campaign: ${errorMessage}`);
        } finally {
            hideLoader();
        }
    };

    // Refresh campaigns list
    const refreshCampaigns = async () => {
        try {
            const token = localStorage.getItem("authToken");
            const response = await axios.get(
                `https://backend.maamaas.com/promotions/api/admin/all/campaigns`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            const campaignsArray = Array.isArray(response.data) ? response.data : [response.data];
            setCampaigns(campaignsArray);

            if (viewCampaign) {
                const updatedCampaign = campaignsArray.find(c => c.id === viewCampaign.id || c.campaignId === viewCampaign.id);
                if (updatedCampaign) {
                    setViewCampaign(updatedCampaign);
                }
            }
        } catch (error) {
            console.error("Error refreshing campaigns:", error);
        }
    };

    // Delete campaign with admin API
    const handleDelete = async (campaignId) => {
        if (!window.confirm("Are you sure you want to delete this campaign?")) {
            return;
        }

        try {
            const token = localStorage.getItem("authToken");
            showLoader();

            await axios.delete(
                `https://backend.maamaas.com/promotions/api/admin/campaign/${campaignId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setCampaigns(prev => prev.filter(campaign => campaign.id !== campaignId && campaign.campaignId !== campaignId));

            if (viewCampaign && (viewCampaign.id === campaignId || viewCampaign.campaignId === campaignId)) {
                setShowViewModal(false);
                setViewCampaign(null);
            }

            alert("Campaign deleted successfully!");

        } catch (error) {
            console.error("❌ Error deleting campaign:", error.response?.data || error.message);
            alert(`Failed to delete campaign: ${error.response?.data?.message || error.message}`);
        } finally {
            hideLoader();
        }
    };

    const handleViewCampaign = async (campaign) => {
        const campaignId = campaign.id || campaign.campaignId;
        const fullCampaignDetails = await fetchCampaignById(campaignId);

        if (fullCampaignDetails) {
            setViewCampaign(fullCampaignDetails);
        } else {
            setViewCampaign(campaign);
        }
        setShowRejectionInput(false);
        setRejectionReason('');
        setShowViewModal(true);
    };

    const handleEditDirect = async (campaign) => {
        const campaignId = campaign.id || campaign.campaignId;
        const fullCampaignDetails = await fetchCampaignById(campaignId);

        if (fullCampaignDetails) {
            setSelectedCampaign(fullCampaignDetails);
            setFormData({
                campaignName: fullCampaignDetails.campaignName || "",
                description: fullCampaignDetails.description || "",
                interests: fullCampaignDetails.interests || fullCampaignDetails.targeting?.interests || ["JOBS"],
                goal: fullCampaignDetails.goal || "BRANDING",
                medium: fullCampaignDetails.medium || "APP",
                status: fullCampaignDetails.status || "ACTIVE",
                startDate: fullCampaignDetails.startDate ? fullCampaignDetails.startDate.split('T')[0] : "",
                endDate: fullCampaignDetails.endDate ? fullCampaignDetails.endDate.split('T')[0] : "",
                city: fullCampaignDetails.city || fullCampaignDetails.targeting?.city || "",
                radiusKm: fullCampaignDetails.radiusKm || fullCampaignDetails.targeting?.radiusKm || 100,
                centerLatitude: fullCampaignDetails.centerLatitude || fullCampaignDetails.targeting?.centerLatitude || 17.3850,
                centerLongitude: fullCampaignDetails.centerLongitude || fullCampaignDetails.targeting?.centerLongitude || 78.4867,
                deepLink: fullCampaignDetails.deepLink || "",
                appType: fullCampaignDetails.appType || "FOOD_AND_BEVERAGES",
                mediaType: fullCampaignDetails.mediaType || "IMAGE",
                addDisplayPosition: fullCampaignDetails.addDisplayPosition || "ADD_SCREEN",
                customerId: fullCampaignDetails.customerId || "ADM161220251",
                paymentStatus: fullCampaignDetails.paymentStatus || "PENDING",
                loops: fullCampaignDetails.screenRequest?.loops || 7,
                period: "PER_DAY",
                targeting: {
                    interests: fullCampaignDetails.targeting?.interests || fullCampaignDetails.interests || ["JOBS"],
                    city: fullCampaignDetails.targeting?.city || fullCampaignDetails.city || "",
                    centerLatitude: fullCampaignDetails.targeting?.centerLatitude || fullCampaignDetails.centerLatitude || 17.3850,
                    centerLongitude: fullCampaignDetails.targeting?.centerLongitude || fullCampaignDetails.centerLongitude || 78.4867,
                    radiusKm: fullCampaignDetails.targeting?.radiusKm || fullCampaignDetails.radiusKm || 100

                }
            });

            // Set screen IDs if they exist
            if (fullCampaignDetails.screenRequest) {
                setScreenIds(fullCampaignDetails.screenRequest.screenIds || []);
            }
        } else {
            setSelectedCampaign(campaign);
            setFormData({
                campaignName: campaign.campaignName || "",
                description: campaign.description || "",
                interests: campaign.interests || campaign.targeting?.interests || ["JOBS"],
                goal: campaign.goal || "BRANDING",
                medium: campaign.medium || "APP",
                status: campaign.status || "ACTIVE",
                startDate: campaign.startDate ? campaign.startDate.split('T')[0] : "",
                endDate: campaign.endDate ? campaign.endDate.split('T')[0] : "",
                city: campaign.city || campaign.targeting?.city || "",
                radiusKm: campaign.radiusKm || campaign.targeting?.radiusKm || 100,
                centerLatitude: campaign.centerLatitude || campaign.targeting?.centerLatitude || 17.3850,
                centerLongitude: campaign.centerLongitude || campaign.targeting?.centerLongitude || 78.4867,
                deepLink: campaign.deepLink || "",
                appType: campaign.appType || "FOOD_AND_BEVERAGES",
                mediaType: campaign.mediaType || "IMAGE",
                addDisplayPosition: campaign.addDisplayPosition || "ADD_SCREEN",
                customerId: campaign.customerId || "ADM161220251",
                paymentStatus: campaign.paymentStatus || "PENDING",
                loops: 7,
                period: "PER_DAY",
                targeting: {
                    interests: campaign.targeting?.interests || campaign.interests || ["JOBS"],
                    city: campaign.targeting?.city || campaign.city || "",
                    centerLatitude: campaign.targeting?.centerLatitude || campaign.centerLatitude || 17.3850,
                    centerLongitude: campaign.targeting?.centerLongitude || campaign.centerLongitude || 78.4867,
                    radiusKm: campaign.targeting?.radiusKm || campaign.radiusKm || 100
                }
            });
        }
        setCreativeFile(null);
        setShowEditModal(true);
    };

    // MODIFIED: handleAddCampaign - Only send specified fields for DIGITAL
    const handleAddCampaign = async (e) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem("authToken");
            showLoader();

            if (!token) {
                alert("Session expired. Please login again.");
                return;
            }

            const customerId = formData.customerId || localStorage.getItem("customerId") || "ADM161220251";

            let campaignData;

            if (formData.medium === "DIGITAL") {
                // For DIGITAL medium, only send the specified fields
                campaignData = {
                    screenRequest: {
                        durationSeconds: creativeDuration || 20,
                        period: "PER_DAY",
                        screenIds: screenIds
                    },
                    endDate: formData.endDate ? new Date(formData.endDate).toISOString() : new Date().toISOString(),
                    city: formData.city || "",
                    startDate: formData.startDate ? new Date(formData.startDate).toISOString() : new Date().toISOString(),
                    status: formData.status,
                    goal: formData.goal,
                    medium: formData.medium,
                    customerId: customerId,
                    createdAt: new Date().toISOString(),
                    campaignName: formData.campaignName,
                    description: formData.description,
                    mediaType: formData.mediaType,
                    subGoal: formData.subGoal,
                };
            } else {
                // For other mediums, keep the existing structure
                const selectedPosition = formData.addDisplayPosition;
                const resolution = displayPositionRatios[selectedPosition]?.resolution || "R_720P";

                campaignData = {
                    campaignName: formData.campaignName,
                    description: formData.description,
                    subGoal: formData.subGoal,
                    goal: formData.goal,
                    medium: formData.medium,
                    status: formData.status,
                    startDate: formData.startDate ? new Date(formData.startDate).toISOString() : new Date().toISOString(),
                    endDate: formData.endDate ? new Date(formData.endDate).toISOString() : new Date().toISOString(),
                    customerId: customerId,
                    appType: formData.appType,
                    deepLink: formData.deepLink || "",
                    addDisplayPosition: formData.addDisplayPosition,
                    resolution: resolution,
                    mediaType: formData.mediaType,
                    interests: formData.interests,
                    city: formData.city || "",
                    centerLatitude: parseFloat(formData.centerLatitude) || 17.3850,
                    centerLongitude: parseFloat(formData.centerLongitude) || 78.4867,
                    radiusKm: parseFloat(formData.radiusKm) || 100,
                    paymentStatus: formData.paymentStatus || "PENDING",
                    targetAudience: formData.targetAudience || ["Users"],
                    targeting: {
                        interests: formData.interests,
                        city: formData.city || "",
                        centerLatitude: parseFloat(formData.centerLatitude) || 17.3850,
                        centerLongitude: parseFloat(formData.centerLongitude) || 78.4867,
                        radiusKm: parseFloat(formData.radiusKm) || 100,
                        targetAudience: formData.targetAudience || ["Users"],

                    },
                    totalBudget: 0,
                    likesCount: 0,
                    viewsCount: 0,
                    sharesCount: 0,
                    savesCount: 0,
                    commentsCount: 0,
                    createdAt: new Date().toISOString(),
                    vendorId: 0,
                    dishId: 0,
                    discountPercentage: 0,
                    calculatedAmount: 0,
                    approvalStatus: "PENDING"
                };
            }

            console.log("Submitting campaign:", campaignData);

            const fd = new FormData();

            fd.append(
                "campaign",
                new Blob([JSON.stringify(campaignData)], { type: "application/json" })
            );

            if (creativeFile) {
                fd.append("creative", creativeFile, creativeFile.name);
            } else {
                fd.append("creative", new Blob([]), "");
            }

            const response = await axios.post(
                `https://backend.maamaas.com/promotions/api/user/create/campaign`,
                fd,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data"
                    }
                }
            );

            setCampaigns(prev => [response.data, ...prev]);
            alert("Campaign created successfully!");

            resetForm();
            setShowAddModal(false);

        } catch (error) {
            console.error("❌ Error creating campaign:", error.response?.data || error.message);
            alert(`Failed to create campaign: ${error.response?.data?.message || error.message}`);
        } finally {
            hideLoader();
        }
    };

    // MODIFIED: handleEditCampaign - Only send specified fields for DIGITAL
    const handleEditCampaign = async (e) => {
        e.preventDefault();

        try {
            const token = localStorage.getItem("authToken");
            showLoader();

            if (!token) {
                alert("Session expired. Please login again.");
                return;
            }

            const customerId = formData.customerId || localStorage.getItem("customerId") || "ADM161220251";

            let campaignData;

            if (formData.medium === "DIGITAL") {
                // For DIGITAL medium, only send the specified fields
                campaignData = {
                    ...selectedCampaign,
                    screenRequest: {
                        durationSeconds: creativeDuration || selectedCampaign.screenRequest?.durationSeconds || 20,
                        period: "PER_DAY",
                        screenIds: screenIds.length > 0 ? screenIds : (selectedCampaign.screenRequest?.screenIds || [])
                    },
                    endDate: formData.endDate ? new Date(formData.endDate).toISOString() : selectedCampaign.endDate,
                    city: formData.city || selectedCampaign.city || "",
                    startDate: formData.startDate ? new Date(formData.startDate).toISOString() : selectedCampaign.startDate,
                    status: formData.status,
                    goal: formData.goal,
                    medium: formData.medium,
                    customerId: customerId,
                    campaignName: formData.campaignName,
                    description: formData.description,
                    mediaType: formData.mediaType
                };
            } else {
                // For other mediums, keep the existing structure
                const selectedPosition = formData.addDisplayPosition;
                const resolution = displayPositionRatios[selectedPosition]?.resolution || "R_720P";

                campaignData = {
                    ...selectedCampaign,
                    campaignName: formData.campaignName,
                    description: formData.description,
                    goal: formData.goal,
                    medium: formData.medium,
                    status: formData.status,
                    startDate: formData.startDate ? new Date(formData.startDate).toISOString() : selectedCampaign.startDate,
                    endDate: formData.endDate ? new Date(formData.endDate).toISOString() : selectedCampaign.endDate,
                    customerId: customerId,
                    appType: formData.appType,
                    deepLink: formData.deepLink || selectedCampaign.deepLink,
                    addDisplayPosition: formData.addDisplayPosition,
                    resolution: resolution,
                    mediaType: formData.mediaType,
                    interests: formData.interests,
                    city: formData.city || selectedCampaign.targeting?.city || "",
                    centerLatitude: parseFloat(formData.centerLatitude) || selectedCampaign.targeting?.centerLatitude || 17.3850,
                    centerLongitude: parseFloat(formData.centerLongitude) || selectedCampaign.targeting?.centerLongitude || 78.4867,
                    radiusKm: parseFloat(formData.radiusKm) || selectedCampaign.targeting?.radiusKm || 100,
                    paymentStatus: formData.paymentStatus || selectedCampaign.paymentStatus || "PENDING",
                    targeting: {
                        interests: formData.interests,
                        city: formData.city || selectedCampaign.targeting?.city || "",
                        centerLatitude: parseFloat(formData.centerLatitude) || selectedCampaign.targeting?.centerLatitude || 17.3850,
                        centerLongitude: parseFloat(formData.centerLongitude) || selectedCampaign.targeting?.centerLongitude || 78.4867,
                        radiusKm: parseFloat(formData.radiusKm) || selectedCampaign.targeting?.radiusKm || 100
                    }
                };
            }

            console.log("Updating campaign:", campaignData);

            setCampaigns(prev =>
                prev.map(campaign =>
                    campaign.id === selectedCampaign.id || campaign.campaignId === selectedCampaign.id
                        ? campaignData
                        : campaign
                )
            );

            if (viewCampaign && (viewCampaign.id === selectedCampaign.id || viewCampaign.campaignId === selectedCampaign.id)) {
                setViewCampaign(campaignData);
            }

            alert("Campaign updated successfully!");
            setShowEditModal(false);
            setSelectedCampaign(null);
            resetForm();

        } catch (error) {
            console.error("❌ Error updating campaign:", error.response?.data || error.message);
            alert(`Failed to update campaign: ${error.response?.data?.message || error.message}`);
        } finally {
            hideLoader();
        }
    };

    const resetForm = () => {
        setFormData({
            campaignName: "",
            description: "",
            interests: ["JOBS"],
            goal: "BRANDING",
            medium: "APP",
            status: "ACTIVE",
            startDate: "",
            endDate: "",
            city: "",
            radiusKm: 100,
            centerLatitude: 17.3850,
            centerLongitude: 78.4867,
            deepLink: "",
            appType: "FOOD_AND_BEVERAGES",
            mediaType: "IMAGE",
            addDisplayPosition: "ADD_SCREEN",
            customerId: "ADM161220251",
            paymentStatus: "PENDING",
            loops: 7,
            period: "PER_DAY",
            targeting: {
                interests: ["JOBS"],
                city: "",
                centerLatitude: 17.3850,
                centerLongitude: 78.4867,
                radiusKm: 100
            }
        });
        setCreativeFile(null);
        setCreativeResolution(null);
        setCreativeDuration(null);
        setCreativeValidationMessages([]);
        setScreenIds([]);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleDateString("en-GB", {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        }).replace(/\//g, '-');
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleString("en-GB", {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getStatusColor = (status) => {
        const colors = {
            'DRAFT': '#9E9E9E',
            'SCHEDULED': '#FF9800',
            'ACTIVE': '#4CAF50',
            'PAUSED': '#FFC107',
            'COMPLETED': '#2196F3',
            'CANCELLED': '#f44336'
        };
        return colors[status] || '#9E9E9E';
    };

    const getApprovalStatusColor = (status) => {
        const colors = {
            'PENDING': '#FF9800',
            'APPROVED': '#4CAF50',
            'REJECTED': '#f44336'
        };
        return colors[status] || '#9E9E9E';
    };

    const getPaymentStatusColor = (status) => {
        const colors = {
            'PENDING': '#FF9800',
            'PAID': '#4CAF50',
            'FAILED': '#f44336',
            'REFUNDED': '#9C27B0',
            'CANCELLED': '#9E9E9E'
        };
        return colors[status] || '#9E9E9E';
    };

    const openMediaPreview = (url) => {
        if (!url) return;
        const videoExtensions = ['mp4', 'webm', 'ogg', 'mov', 'avi', 'mkv'];
        const extension = url.split('.').pop()?.toLowerCase().split('?')[0];
        const isVideo = videoExtensions.includes(extension) || url.includes('video') || url.includes('mp4');

        console.log("Opening media:", { url, extension, isVideo });

        setPreviewMediaUrl(url);
        setPreviewMediaType(isVideo ? 'video' : 'image');
        setShowMediaPreview(true);
    };

    const closeMediaPreview = () => {
        setShowMediaPreview(false);
        setPreviewMediaUrl('');
        setPreviewMediaType('');
    };

    const modalOverlayStyle = {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        padding: isMobile ? '10px' : '20px',
        overflow: 'auto',
    };

    const modalContentStyle = {
        backgroundColor: '#fff',
        borderRadius: '8px',
        width: '100%',
        maxWidth: isMobile ? '95%' : '600px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        animation: 'fadeIn 0.3s ease-out',
    };

    const modalHeaderStyle = {
        padding: isMobile ? '15px' : '20px',
        borderBottom: '1px solid #eee',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        backgroundColor: '#fff',
        zIndex: 10,
    };

    const modalBodyStyle = {
        padding: isMobile ? '15px' : '20px',
    };

    const tdStyle = {
        border: "1px solid #ccc",
        padding: "10px",
        textAlign: "left"
    };

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeIn {
                from {
                    opacity: 0;
                    transform: translateY(-20px);
                }
                to {
                    opacity: 1;
                    transform: translateY(0);
                }
            }
            
            @keyframes fadeOut {
                from {
                    opacity: 1;
                    transform: translateY(0);
                }
                to {
                    opacity: 0;
                    transform: translateY(-20px);
                }
            }
            
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
            
            .modal-enter {
                animation: fadeIn 0.3s ease-out;
            }
            
            .modal-exit {
                animation: fadeOut 0.3s ease-out;
            }
        `;
        document.head.appendChild(style);

        return () => {
            document.head.removeChild(style);
        };
    }, []);

    return (
        <div
            style={{
                maxWidth: '1400px',
                width: '100%',
                margin: '0 auto',
                padding: isMobile ? '15px' : '20px',
                position: 'relative',
            }}
        >
            {/* Media Preview Modal */}
            {showMediaPreview && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 10000,
                    padding: '20px',
                }} onClick={closeMediaPreview}>
                    <div style={{
                        position: 'relative',
                        maxWidth: '90%',
                        maxHeight: '90%',
                    }} onClick={e => e.stopPropagation()}>
                        <button
                            style={{
                                position: 'absolute',
                                top: '-40px',
                                right: '-40px',
                                background: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                fontSize: '20px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 10001,
                            }}
                            onClick={closeMediaPreview}
                        >
                            ✕
                        </button>
                        {previewMediaType === 'video' ? (
                            <video
                                key={previewMediaUrl}
                                controls
                                autoPlay
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    borderRadius: '8px',
                                }}
                            >
                                <source src={previewMediaUrl} type="video/mp4" />
                                <source src={previewMediaUrl} type="video/webm" />
                                <source src={previewMediaUrl} type="video/ogg" />
                                Your browser does not support the video tag.
                            </video>
                        ) : (
                            <img
                                src={previewMediaUrl}
                                alt="Preview"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    borderRadius: '8px',
                                }}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Approval Modal */}
            {showApprovalModal && selectedCampaignForApproval && (
                <div style={modalOverlayStyle} onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowApprovalModal(false);
                        setSelectedCampaignForApproval(null);
                        setApprovalData({
                            campaignId: null,
                            approvalStatus: "PENDING",
                            rejectionReason: ""
                        });
                    }
                }}>
                    <div style={modalContentStyle} className="modal-enter">
                        <div style={modalHeaderStyle}>
                            <h3 style={{
                                fontSize: isMobile ? '16px' : '18px',
                                fontWeight: 'bold',
                                margin: 0,
                                color: '#333'
                            }}>
                                {approvalData.approvalStatus === "REJECTED" ? "Reject Campaign" : "Update Campaign Approval"}
                            </h3>
                            <button
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontWeight: 'bold',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                }}
                                onClick={() => {
                                    setShowApprovalModal(false);
                                    setSelectedCampaignForApproval(null);
                                    setApprovalData({
                                        campaignId: null,
                                        approvalStatus: "PENDING",
                                        rejectionReason: ""
                                    });
                                }}
                                onMouseOver={e => e.target.style.backgroundColor = '#f0f0f0'}
                                onMouseOut={e => e.target.style.backgroundColor = 'transparent'}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            <form onSubmit={handleApprovalUpdate}>
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '10px', fontSize: '15px' }}>
                                        Campaign: {selectedCampaignForApproval.campaignName}
                                    </h4>
                                    <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                                        ID: {selectedCampaignForApproval.id || selectedCampaignForApproval.campaignId}
                                    </p>
                                </div>

                                {approvalData.approvalStatus !== "REJECTED" && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={labelStyle}>Approval Status *</label>
                                        <select
                                            name="approvalStatus"
                                            value={approvalData.approvalStatus}
                                            onChange={handleApprovalChange}
                                            required
                                            style={inputStyle}
                                        >
                                            {approvalStatusOptions.map((option) => (
                                                <option key={option} value={option}>
                                                    {option}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {approvalData.approvalStatus === "REJECTED" && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <label style={labelStyle}>Rejection Reason *</label>
                                        <textarea
                                            name="rejectionReason"
                                            placeholder="Please provide reason for rejection"
                                            value={approvalData.rejectionReason}
                                            onChange={handleApprovalChange}
                                            required={approvalData.approvalStatus === "REJECTED"}
                                            rows="4"
                                            style={{ ...inputStyle, resize: 'vertical' }}
                                        />
                                    </div>
                                )}

                                <div style={{
                                    marginBottom: '20px',
                                    padding: '12px',
                                    backgroundColor: '#f5f5f5',
                                    borderRadius: '4px'
                                }}>
                                    <strong style={{ display: 'block', marginBottom: '5px' }}>Current Approval Status:</strong>
                                    <span style={{
                                        padding: "4px 8px",
                                        borderRadius: "4px",
                                        backgroundColor: getApprovalStatusColor(selectedCampaignForApproval.approvalStatus || 'PENDING'),
                                        color: "white",
                                        fontSize: "12px",
                                        fontWeight: "500",
                                        display: 'inline-block'
                                    }}>
                                        {selectedCampaignForApproval.approvalStatus || 'PENDING'}
                                    </span>
                                </div>

                                <div style={{
                                    display: 'flex',
                                    gap: '10px',
                                    marginTop: '20px',
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: '#fff',
                                    padding: '10px 0',
                                    borderTop: '1px solid #eee'
                                }}>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px',
                                            backgroundColor: approvalData.approvalStatus === "REJECTED" ? '#f44336' : 'rgb(230 109 51)',
                                            color: 'white',
                                            fontWeight: '600',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            flex: 1,
                                            fontSize: '14px',
                                        }}
                                        onMouseOver={e => e.target.style.backgroundColor = approvalData.approvalStatus === "REJECTED" ? '#d32f2f' : '#9b4ab9'}
                                        onMouseOut={e => e.target.style.backgroundColor = approvalData.approvalStatus === "REJECTED" ? '#f44336' : 'rgb(230 109 51)'}
                                    >
                                        {approvalData.approvalStatus === "REJECTED" ? 'Reject Campaign' : 'Update Approval Status'}
                                    </button>
                                    <button
                                        type="button"
                                        style={{
                                            padding: '12px',
                                            backgroundColor: '#f5f5f5',
                                            color: '#333',
                                            fontWeight: '600',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            flex: 1,
                                            fontSize: '14px',
                                        }}
                                        onClick={() => {
                                            setShowApprovalModal(false);
                                            setSelectedCampaignForApproval(null);
                                            setApprovalData({
                                                campaignId: null,
                                                approvalStatus: "PENDING",
                                                rejectionReason: ""
                                            });
                                        }}
                                        onMouseOver={e => e.target.style.backgroundColor = '#e0e0e0'}
                                        onMouseOut={e => e.target.style.backgroundColor = '#f5f5f5'}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

           
            {showViewModal && viewCampaign && (
                <div style={modalOverlayStyle} onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowViewModal(false);
                        setViewCampaign(null);
                        setShowRejectionInput(false);
                        setRejectionReason('');
                    }
                }}>
                    <div style={modalContentStyle} className="modal-enter">
                        <div style={modalHeaderStyle}>
                            <h3 style={{
                                fontSize: isMobile ? '16px' : '18px',
                                fontWeight: 'bold',
                                margin: 0,
                                color: '#333'
                            }}>
                                Campaign Details
                            </h3>
                            <button
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontWeight: 'bold',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                }}
                                onClick={() => {
                                    setShowViewModal(false);
                                    setViewCampaign(null);
                                    setShowRejectionInput(false);
                                    setRejectionReason('');
                                }}
                                onMouseOver={e => e.target.style.backgroundColor = '#f0f0f0'}
                                onMouseOut={e => e.target.style.backgroundColor = 'transparent'}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '15px', fontSize: '16px' }}>Basic Information</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', fontSize: '14px' }}>
                                    <strong>ID:</strong> <span>{viewCampaign.id || viewCampaign.campaignId}</span>
                                    <strong>Promotion Name:</strong> <span>{viewCampaign.campaignName}</span>
                                    <strong>Description:</strong> <span>{viewCampaign.description}</span>
                                    <strong>Goal:</strong> <span>{viewCampaign.goal}</span>
                                    <strong>Medium:</strong> <span>{viewCampaign.medium}</span>
                                    <strong>Status:</strong>
                                    <span>
                                        <span style={{
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: getStatusColor(viewCampaign.status),
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "500",
                                            display: 'inline-block'
                                        }}>
                                            {viewCampaign.status}
                                        </span>
                                    </span>
                                    <strong>Approval Status:</strong>
                                    <span>
                                        <span style={{
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: getApprovalStatusColor(viewCampaign.approvalStatus || 'PENDING'),
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "500",
                                            display: 'inline-block'
                                        }}>
                                            {viewCampaign.approvalStatus || 'PENDING'}
                                        </span>
                                    </span>
                                    <strong>Payment Status:</strong>
                                    <span>
                                        <span style={{
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: getPaymentStatusColor(viewCampaign.paymentStatus || 'PENDING'),
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "500",
                                            display: 'inline-block'
                                        }}>
                                            {viewCampaign.paymentStatus || 'PENDING'}
                                        </span>
                                    </span>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '15px', fontSize: '16px' }}>Dates</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', fontSize: '14px' }}>
                                    <strong>Start Date:</strong> <span>{formatDateTime(viewCampaign.startDate)}</span>
                                    <strong>End Date:</strong> <span>{formatDateTime(viewCampaign.endDate)}</span>
                                    <strong>Created At:</strong> <span>{formatDateTime(viewCampaign.createdAt)}</span>
                                    <strong>Updated At:</strong> <span>{viewCampaign.updatedAt ? formatDateTime(viewCampaign.updatedAt) : 'Not updated'}</span>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '15px', fontSize: '16px' }}>Targeting</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', fontSize: '14px' }}>
                                    <strong>Interests:</strong>
                                    <span>
                                        {viewCampaign.targeting?.interests && viewCampaign.targeting.interests.length > 0
                                            ? viewCampaign.targeting.interests.join(', ')
                                            : viewCampaign.interests && viewCampaign.interests.length > 0
                                                ? viewCampaign.interests.join(', ')
                                                : 'None'}
                                    </span>
                                    <strong>City:</strong> <span>{viewCampaign.city || viewCampaign.targeting?.city || 'Not specified'}</span>
                                    <strong>Center Latitude:</strong> <span>{viewCampaign.centerLatitude || viewCampaign.targeting?.centerLatitude || 'Not specified'}</span>
                                    <strong>Center Longitude:</strong> <span>{viewCampaign.centerLongitude || viewCampaign.targeting?.centerLongitude || 'Not specified'}</span>
                                    <strong>Radius:</strong> <span>{viewCampaign.radiusKm || viewCampaign.targeting?.radiusKm ? `${viewCampaign.radiusKm || viewCampaign.targeting?.radiusKm} km` : 'Not specified'}</span>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '15px', fontSize: '16px' }}>Campaign Details</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', fontSize: '14px' }}>
                                    <strong>App Type:</strong> <span>{viewCampaign.appType}</span>
                                    <strong>Deep Link:</strong> <span>{viewCampaign.deepLink || 'Not specified'}</span>
                                    <strong>Add Display Position:</strong> <span>{viewCampaign.addDisplayPosition}</span>
                                    <strong>Resolution:</strong> <span>{viewCampaign.resolution || 'Auto-set'}</span>
                                    <strong>Media Type:</strong> <span>{viewCampaign.mediaType || 'Not specified'}</span>
                                    <strong>Customer ID:</strong> <span>{viewCampaign.customerId}</span>
                                </div>
                            </div>

                            {/* Show Digital Screen Settings if medium is DIGITAL */}
                            {viewCampaign.medium === "DIGITAL" && viewCampaign.screenRequest && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '15px', fontSize: '16px' }}>Digital Screen Settings</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', fontSize: '14px' }}>
                                        <strong>Duration Seconds:</strong>
                                        <span>{viewCampaign.screenRequest.durationSeconds || 20}s</span>
                                        <strong>Period:</strong>
                                        <span>{viewCampaign.screenRequest.period || 'PER_DAY'}</span>
                                        <strong>Screen IDs:</strong>
                                        <span>
                                            {viewCampaign.screenRequest.screenIds && viewCampaign.screenRequest.screenIds.length > 0
                                                ? viewCampaign.screenRequest.screenIds.join(', ')
                                                : 'None'}
                                        </span>
                                    </div>
                                </div>
                            )}

                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '15px', fontSize: '16px' }}>Statistics</h4>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', fontSize: '14px' }}>
                                    <strong>Total Budget:</strong> <span>₹{viewCampaign.totalBudget?.toFixed(2) || '0.00'}</span>
                                    <strong>Likes Count:</strong> <span>{viewCampaign.likesCount || 0}</span>
                                    <strong>Views Count:</strong> <span>{viewCampaign.viewsCount || 0}</span>
                                    <strong>Shares Count:</strong> <span>{viewCampaign.sharesCount || 0}</span>
                                    <strong>Saves Count:</strong> <span>{viewCampaign.savesCount || 0}</span>
                                    <strong>Comments Count:</strong> <span>{viewCampaign.commentsCount || 0}</span>
                                </div>
                            </div>

                            {viewCampaign.imageUrl && (
                                <div style={{ marginBottom: '20px' }}>
                                    <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '15px', fontSize: '16px' }}>Creative</h4>
                                    <button
                                        onClick={() => openMediaPreview(viewCampaign.imageUrl)}
                                        style={{
                                            backgroundColor: "#4CAF50",
                                            color: "white",
                                            border: "none",
                                            padding: "10px 16px",
                                            borderRadius: "4px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            fontWeight: "500",
                                        }}
                                        onMouseOver={e => e.target.style.backgroundColor = '#45a049'}
                                        onMouseOut={e => e.target.style.backgroundColor = '#4CAF50'}
                                    >
                                        View Creative
                                    </button>
                                </div>
                            )}

                            {/* Approval Actions */}
                            <div style={{
                                marginTop: '20px',
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #e0e0e0'
                            }}>
                                <h4 style={{ color: 'rgb(230 109 51)', marginBottom: '10px', fontSize: '14px' }}>Approval Actions</h4>

                                {/* Show rejection reason input when rejecting */}
                                {showRejectionInput ? (
                                    <div style={{ marginBottom: '15px' }}>
                                        <label style={{ ...labelStyle, fontSize: '13px', marginBottom: '8px' }}>
                                            Rejection Reason *
                                        </label>
                                        <textarea
                                            placeholder="Please provide reason for rejection"
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            rows="3"
                                            style={{
                                                ...inputStyle,
                                                resize: 'vertical',
                                                fontSize: '13px',
                                                padding: '8px',
                                                marginBottom: '10px'
                                            }}
                                        />
                                        <div style={{
                                            display: 'flex',
                                            gap: '10px',
                                            justifyContent: 'flex-end'
                                        }}>
                                            <button
                                                style={{
                                                    backgroundColor: "#f44336",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "6px 12px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                                onClick={() => {
                                                    if (!rejectionReason.trim()) {
                                                        alert("Please provide a rejection reason");
                                                        return;
                                                    }
                                                    handleDirectReject(viewCampaign);
                                                }}
                                                onMouseOver={e => e.target.style.backgroundColor = '#d32f2f'}
                                                onMouseOut={e => e.target.style.backgroundColor = '#f44336'}
                                            >
                                                Submit Rejection
                                            </button>
                                            <button
                                                style={{
                                                    backgroundColor: "#9e9e9e",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "6px 12px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                                onClick={() => {
                                                    setShowRejectionInput(false);
                                                    setRejectionReason('');
                                                }}
                                                onMouseOver={e => e.target.style.backgroundColor = '#757575'}
                                                onMouseOut={e => e.target.style.backgroundColor = '#9e9e9e'}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        gap: '10px',
                                        flexWrap: 'wrap',
                                        justifyContent: 'center'
                                    }}>
                                        {/* Approve Button */}
                                        <button
                                            style={{
                                                backgroundColor: "#4CAF50",
                                                color: "white",
                                                border: "none",
                                                padding: "8px 16px",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                                flex: '0 1 auto',
                                                minWidth: '100px',
                                                opacity: viewCampaign.approvalStatus === 'APPROVED' ? 0.5 : 1
                                            }}
                                            onClick={() => handleDirectApprove(viewCampaign)}
                                            disabled={viewCampaign.approvalStatus === 'APPROVED'}
                                            onMouseOver={e => {
                                                if (viewCampaign.approvalStatus !== 'APPROVED') {
                                                    e.target.style.backgroundColor = '#45a049';
                                                }
                                            }}
                                            onMouseOut={e => {
                                                if (viewCampaign.approvalStatus !== 'APPROVED') {
                                                    e.target.style.backgroundColor = '#4CAF50';
                                                }
                                            }}
                                        >
                                            {viewCampaign.approvalStatus === 'APPROVED' ? '✓ Already Approved' : 'Approve Campaign'}
                                        </button>

                                        {/* Reject Button */}
                                        <button
                                            style={{
                                                backgroundColor: "#f44336",
                                                color: "white",
                                                border: "none",
                                                padding: "8px 16px",
                                                borderRadius: "4px",
                                                cursor: "pointer",
                                                fontSize: "12px",
                                                fontWeight: "500",
                                                flex: '0 1 auto',
                                                minWidth: '100px',
                                                opacity: viewCampaign.approvalStatus === 'REJECTED' ? 0.5 : 1
                                            }}
                                            onClick={() => {
                                                if (viewCampaign.approvalStatus !== 'REJECTED') {
                                                    setShowRejectionInput(true);
                                                }
                                            }}
                                            disabled={viewCampaign.approvalStatus === 'REJECTED'}
                                            onMouseOver={e => {
                                                if (viewCampaign.approvalStatus !== 'REJECTED') {
                                                    e.target.style.backgroundColor = '#d32f2f';
                                                }
                                            }}
                                            onMouseOut={e => {
                                                if (viewCampaign.approvalStatus !== 'REJECTED') {
                                                    e.target.style.backgroundColor = '#f44336';
                                                }
                                            }}
                                        >
                                            {viewCampaign.approvalStatus === 'REJECTED' ? '✗ Already Rejected' : 'Reject Campaign'}
                                        </button>
                                    </div>
                                )}

                                <p style={{
                                    fontSize: '11px',
                                    color: '#666',
                                    marginTop: '8px',
                                    textAlign: 'center',
                                    fontStyle: 'italic'
                                }}>
                                    {viewCampaign.approvalStatus === 'APPROVED'
                                        ? 'This campaign has already been approved.'
                                        : viewCampaign.approvalStatus === 'REJECTED'
                                            ? 'This campaign has already been rejected.'
                                            : showRejectionInput
                                                ? 'Please provide a reason for rejection'
                                                : 'Approve this campaign to make it active or reject with a reason.'}
                                </p>
                            </div>

                            {/* Close Button */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                                <button
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#9e9e9e',
                                        color: 'white',
                                        fontWeight: '600',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                    }}
                                    onClick={() => {
                                        setShowViewModal(false);
                                        setViewCampaign(null);
                                        setShowRejectionInput(false);
                                        setRejectionReason('');
                                    }}
                                    onMouseOver={e => e.target.style.backgroundColor = '#757575'}
                                    onMouseOut={e => e.target.style.backgroundColor = '#9e9e9e'}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )} 
           
            {/* Create Campaign Button */}
            {/* <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
                flexWrap: 'wrap',
                gap: '10px'
             }}>
                <button
                    style={{
                        padding: isMobile ? '8px 14px' : '10px 16px',
                        backgroundColor: '#1976d2',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: isMobile ? '13px' : '14px',
                        transition: 'background-color 0.3s',
                    }}
                    onClick={() => {
                        resetForm();
                        setShowAddModal(true);
                    }}
                    onMouseOver={e => e.target.style.backgroundColor = '#1565c0'}
                    onMouseOut={e => e.target.style.backgroundColor = '#1976d2'}
                >
                    + Create Promotions
                </button>
            </div> */}

            {/* Table Section */}
            <div style={{
                overflowX: 'auto',
                overflowY: 'auto',
                width: '100%',
                maxHeight: '63vh',
                maxWidth: '1220px',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                border: '1px solid #e0e0e0',
            }}>
                <table
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        minWidth: '1500px',
                    }}
                >
                    <thead>
                        <tr>
                            {[
                                "S.No",
                                "Promotion Name",
                                "Description",
                                "Interests",
                                "Goal",
                                "Medium",
                                "Campaign Status",
                                "Approval Status",
                                "Payment Status",
                                "Start Date",
                                "End Date",
                                "City",
                                "Radius",
                                "App Type",
                                "Media Type",
                                "Display Position",
                                "Resolution",
                                "Customer ID",
                                "Creative",
                                "Actions",
                            ].map((head, i) => (
                                <th
                                    key={i}
                                    style={{
                                        border: "1px solid #e0e0e0",
                                        padding: isMobile ? "8px" : "12px",
                                        backgroundColor: "rgb(230 109 51)",
                                        fontSize: isMobile ? "12px" : "14px",
                                        textAlign: "center",
                                        whiteSpace: "nowrap",
                                        color: "white",
                                        fontWeight: "600",
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 10,
                                    }}
                                >
                                    {head}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="20" style={{ textAlign: "center", padding: "40px" }}>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexDirection: 'column',
                                        gap: '10px'
                                    }}>
                                        <div style={{
                                            width: '40px',
                                            height: '40px',
                                            border: '3px solid #f3f3f3',
                                            borderTop: '3px solid rgb(230 109 51)',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}></div>
                                        <span style={{ color: '#666' }}>Loading campaigns...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : campaigns.length === 0 ? (
                            <tr>
                                <td colSpan="20" style={{ textAlign: "center", padding: "40px" }}>
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '15px',
                                        color: '#666'
                                    }}>
                                        <span>No campaigns found.</span>
                                        <button
                                            onClick={() => {
                                                resetForm();
                                                setShowAddModal(true);
                                            }}
                                            style={{
                                                padding: '8px 16px',
                                                backgroundColor: '#1976d2',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: '500'
                                            }}
                                        >
                                            Create Your First Campaign
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            campaigns.map((campaign, index) => (
                                <tr key={campaign.id || campaign.campaignId || index} style={{
                                    backgroundColor: index % 2 === 0 ? '#f9f9f9' : 'white',
                                    transition: 'background-color 0.2s',
                                }}>
                                    <td style={tdStyle}>{index + 1}</td>
                                    <td style={tdStyle}>{campaign.campaignName || "-"}</td>
                                    <td style={tdStyle}>{campaign.description || "-"}</td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: "#607d8b",
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "500",
                                            display: 'inline-block'
                                        }}>
                                            {campaign.interests && campaign.interests.length > 0
                                                ? campaign.interests.join(', ')
                                                : campaign.targeting?.interests && campaign.targeting.interests.length > 0
                                                    ? campaign.targeting.interests.join(', ')
                                                    : "-"}
                                        </span>
                                    </td>
                                    <td style={tdStyle}>{campaign.goal || "-"}</td>
                                    <td style={tdStyle}>{campaign.medium || "-"}</td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: getStatusColor(campaign.status),
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "500",
                                            display: 'inline-block'
                                        }}>
                                            {campaign.status || "DRAFT"}
                                        </span>
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: getApprovalStatusColor(campaign.approvalStatus || 'PENDING'),
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "500",
                                            display: 'inline-block'
                                        }}>
                                            {campaign.approvalStatus || 'PENDING'}
                                        </span>
                                    </td>
                                    <td style={tdStyle}>
                                        <span style={{
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            backgroundColor: getPaymentStatusColor(campaign.paymentStatus || 'PENDING'),
                                            color: "white",
                                            fontSize: "12px",
                                            fontWeight: "500",
                                            display: 'inline-block'
                                        }}>
                                            {campaign.paymentStatus || 'PENDING'}
                                        </span>
                                    </td>
                                    <td style={tdStyle}>{formatDate(campaign.startDate)}</td>
                                    <td style={tdStyle}>{formatDate(campaign.endDate)}</td>
                                    <td style={tdStyle}>{campaign.city || campaign.targeting?.city || "-"}</td>
                                    <td style={tdStyle}>{campaign.radiusKm || campaign.targeting?.radiusKm ? `${campaign.radiusKm || campaign.targeting?.radiusKm} km` : "-"}</td>
                                    <td style={tdStyle}>{campaign.appType || "-"}</td>
                                    <td style={tdStyle}>{campaign.mediaType || "-"}</td>
                                    <td style={tdStyle}>{campaign.addDisplayPosition || "-"}</td>
                                    <td style={tdStyle}>{campaign.resolution || "Auto-set"}</td>
                                    <td style={tdStyle}>{campaign.customerId || "-"}</td>
                                    <td style={tdStyle}>
                                        {campaign.imageUrl ? (
                                            <button
                                                onClick={() => openMediaPreview(campaign.imageUrl)}
                                                style={{
                                                    backgroundColor: "#4CAF50",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "6px 12px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                                onMouseOver={e => e.target.style.backgroundColor = '#45a049'}
                                                onMouseOut={e => e.target.style.backgroundColor = '#4CAF50'}
                                            >
                                                View Creative
                                            </button>
                                        ) : (
                                            "-"
                                        )}
                                    </td>
                                    <td style={tdStyle}>
                                        <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                                            {/* View Button */}
                                            <button
                                                style={{
                                                    backgroundColor: "#4CAF50",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "8px 12px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                                onClick={() => handleViewCampaign(campaign)}
                                                onMouseOver={e => e.target.style.backgroundColor = '#45a049'}
                                                onMouseOut={e => e.target.style.backgroundColor = '#4CAF50'}
                                            >
                                                View
                                            </button>

                                            {/* Edit Button */}
                                            <button
                                                style={{
                                                    backgroundColor: "#2196F3",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "8px 12px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                                onClick={() => handleEditDirect(campaign)}
                                                onMouseOver={e => e.target.style.backgroundColor = '#1976d2'}
                                                onMouseOut={e => e.target.style.backgroundColor = '#2196F3'}
                                            >
                                                Edit
                                            </button>

                                            {/* Delete Button */}
                                            <button
                                                style={{
                                                    backgroundColor: "#f44336",
                                                    color: "white",
                                                    border: "none",
                                                    padding: "8px 12px",
                                                    borderRadius: "4px",
                                                    cursor: "pointer",
                                                    fontSize: "12px",
                                                    fontWeight: "500",
                                                }}
                                                onClick={() => handleDelete(campaign.id || campaign.campaignId)}
                                                onMouseOver={e => e.target.style.backgroundColor = '#d32f2f'}
                                                onMouseOut={e => e.target.style.backgroundColor = '#f44336'}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add Campaign Modal */}
            {showAddModal && (
                <div style={modalOverlayStyle} onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowAddModal(false);
                        resetForm();
                    }
                }}>
                    <div style={modalContentStyle} className="modal-enter">
                        <div style={modalHeaderStyle}>
                            <h3 style={{
                                fontSize: isMobile ? '16px' : '18px',
                                fontWeight: 'bold',
                                margin: 0,
                                color: '#333'
                            }}>
                                Create New Campaign
                            </h3>
                            <button
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontWeight: 'bold',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                }}
                                onClick={() => {
                                    setShowAddModal(false);
                                    resetForm();
                                }}
                                onMouseOver={e => e.target.style.backgroundColor = '#f0f0f0'}
                                onMouseOut={e => e.target.style.backgroundColor = 'transparent'}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            <form onSubmit={handleAddCampaign}>
                                {/* Campaign Name */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Promotion Name *</label>
                                    <input
                                        type="text"
                                        name="campaignName"
                                        placeholder="Enter promotion name"
                                        value={formData.campaignName}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    />
                                </div>

                                {/* Goal */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Goal *</label>
                                    <select
                                        name="goal"
                                        value={formData.goal}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    >
                                        {goalOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Sub Goal *</label>

                                    <select
                                        name="subGoal"
                                        value={formData.subGoal}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    >
                                        <option value="">Select Sub Goal</option>

                                        {subGoalOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option.replace(/_/g, " ")}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Medium */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Medium *</label>
                                    <select
                                        name="medium"
                                        value={formData.medium}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    >
                                        {mediumOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Target Audience *</label>

                                    {["Users", "Vendors", "Partners"].map((audience) => (
                                        <label key={audience}>
                                            <input
                                                type="checkbox"
                                                value={audience}
                                                checked={(formData.targetAudience || []).includes(audience)}
                                                onChange={(e) => {
                                                    const { value, checked } = e.target;

                                                    let updated = [...(formData.targetAudience || [])];

                                                    if (checked) {
                                                        updated.push(value);
                                                    } else {
                                                        updated = updated.filter(a => a !== value);
                                                    }

                                                    setFormData(prev => ({
                                                        ...prev,
                                                        targetAudience: updated,
                                                        targeting: {
                                                            ...prev.targeting,
                                                            targetAudience: updated
                                                        }
                                                    }));
                                                }}
                                            />
                                            {audience}
                                        </label>
                                    ))}
                                </div>
                                {/* APP-Specific Fields */}
                                {formData.medium === "APP" && (
                                    <>
                                        {/* Start Date */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Start Date *</label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={formData.startDate}
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* End Date */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>End Date *</label>
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={formData.endDate}
                                                onChange={handleChange}
                                                min={formData.startDate || new Date().toISOString().split('T')[0]}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* Interests (Checkboxes) */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Interests *</label>
                                            <div style={{
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                padding: '12px',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                backgroundColor: '#fff'
                                            }}>
                                                {interestOptions.map((option) => (
                                                    <label
                                                        key={option}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginBottom: '8px',
                                                            cursor: 'pointer',
                                                            padding: '4px',
                                                            borderRadius: '4px',
                                                            backgroundColor: formData.interests.includes(option) ? '#e3f2fd' : 'transparent',
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            value={option}
                                                            checked={formData.interests.includes(option)}
                                                            onChange={(e) => {
                                                                const { value, checked } = e.target;
                                                                let updatedInterests = [...formData.interests];

                                                                if (checked) {
                                                                    updatedInterests.push(value);
                                                                } else {
                                                                    updatedInterests = updatedInterests.filter(interest => interest !== value);
                                                                }

                                                                if (updatedInterests.length === 0) {
                                                                    alert("Please select at least one interest");
                                                                    return;
                                                                }

                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    interests: updatedInterests,
                                                                    targeting: {
                                                                        ...prev.targeting,
                                                                        interests: updatedInterests
                                                                    }
                                                                }));
                                                            }}
                                                            style={{
                                                                marginRight: '10px',
                                                                cursor: 'pointer',
                                                                width: '16px',
                                                                height: '16px'
                                                            }}
                                                        />
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: formData.interests.includes(option) ? '600' : '400'
                                                        }}>
                                                            {option}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                                Selected: {formData.interests.join(', ')}
                                            </small>
                                        </div>

                                        {/* City with search */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>City</label>
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    placeholder="Enter city name"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    style={{ ...inputStyle, flex: 1 }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => goToCity(formData.city)}
                                                    style={{
                                                        padding: '10px 20px',
                                                        backgroundColor: '#4CAF50',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontWeight: '500',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    Search
                                                </button>
                                            </div>
                                        </div>

                                        {/* Location Map */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <label style={labelStyle}>Location</label>
                                                <button
                                                    type="button"
                                                    onClick={getCurrentLocation}
                                                    style={{
                                                        padding: '6px 12px',
                                                        backgroundColor: '#2196F3',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Get Current Location
                                                </button>
                                            </div>
                                            <div style={{ position: 'relative', height: '300px' }}>
                                                <div
                                                    id="add-map"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        borderRadius: '8px',
                                                        border: '1px solid #ddd'
                                                    }}
                                                ></div>
                                                {!locationLoaded && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '8px'
                                                    }}>
                                                        <div style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            border: '3px solid #f3f3f3',
                                                            borderTop: '3px solid #FF5722',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }}></div>
                                                    </div>
                                                )}
                                            </div>
                                            <input
                                                ref={searchInputRef}
                                                type="text"
                                                placeholder="Search for a location"
                                                style={{
                                                    ...inputStyle,
                                                    marginTop: '10px'
                                                }}
                                            />
                                        </div>

                                        {/* Target Radius with Slider */}
                                        <div style={{ marginBottom: '20px', backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <label style={labelStyle}>Target Radius</label>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    backgroundColor: '#FF5722',
                                                    color: 'white',
                                                    borderRadius: '20px',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px'
                                                }}>
                                                    {formData.radiusKm} km
                                                </span>
                                            </div>
                                            <input
                                                type="range"
                                                name="radiusKm"
                                                min="0"
                                                max="100"
                                                step="1"
                                                value={formData.radiusKm}
                                                onChange={handleChange}
                                                style={{ width: '100%', accentColor: '#FF5722' }}
                                            />
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                                                <span>0 km</span>
                                                <span>25 km</span>
                                                <span>50 km</span>
                                                <span>100 km</span>
                                            </div>
                                        </div>

                                        {/* Deep Link */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Deep Link</label>
                                            <input
                                                type="url"
                                                name="deepLink"
                                                placeholder="https://example.com"
                                                value={formData.deepLink}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* App Type */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>App Type *</label>
                                            <select
                                                name="appType"
                                                value={formData.appType}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            >
                                                {appTypeOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option.replace(/_/g, ' ')}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Media Type */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Media Type *</label>
                                            <select
                                                name="mediaType"
                                                value={formData.mediaType}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            >
                                                {mediaTypeOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                                Select the type of media you're uploading
                                            </small>
                                        </div>

                                        {/* Add Display Position */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Display Position *</label>
                                            <select
                                                name="addDisplayPosition"
                                                value={formData.addDisplayPosition}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            >
                                                {displayPositionOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option.replace(/_/g, ' ')}
                                                    </option>
                                                ))}
                                            </select>

                                            {/* Display Position Info Table */}
                                            {formData.addDisplayPosition && (
                                                <div style={{
                                                    marginTop: '15px',
                                                    padding: '15px',
                                                    backgroundColor: '#f0f7ff',
                                                    borderRadius: '8px',
                                                    border: '1px solid #b8daff',
                                                }}>
                                                    <h4 style={{
                                                        margin: '0 0 10px 0',
                                                        fontSize: '14px',
                                                        color: '#004085',
                                                        fontWeight: '600'
                                                    }}>
                                                        📊 Recommended Specifications for {formData.addDisplayPosition.replace(/_/g, ' ')}:
                                                    </h4>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 1fr',
                                                        gap: '10px',
                                                        fontSize: '13px'
                                                    }}>
                                                        <div><strong>Best Ratio:</strong> {displayPositionRatios[formData.addDisplayPosition]?.ratio}</div>
                                                        <div><strong>Auto Resolution:</strong> {displayPositionRatios[formData.addDisplayPosition]?.resolution}</div>
                                                        <div><strong>Description:</strong> {displayPositionRatios[formData.addDisplayPosition]?.description}</div>
                                                        <div><strong>Media Type:</strong> {formData.mediaType}</div>
                                                    </div>
                                                    <p style={{
                                                        margin: '10px 0 0 0',
                                                        fontSize: '12px',
                                                        color: '#28a745',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        ⚡ Resolution will be auto-set based on display position
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Creative File */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Upload File *</label>
                                            <input
                                                type="file"
                                                name="creative"
                                                onChange={handleCreativeChange}
                                                required
                                                style={{
                                                    ...inputStyle,
                                                    padding: '8px',
                                                    cursor: 'pointer'
                                                }}
                                                accept=".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.mkv,.webm"
                                            />

                                            {/* Creative Requirements Box */}
                                            {formData.addDisplayPosition && (
                                                <div style={{
                                                    marginTop: '15px',
                                                    padding: '15px',
                                                    backgroundColor: '#e3f2fd',
                                                    borderLeft: '4px solid #2196f3',
                                                    borderRadius: '4px',
                                                    fontSize: '13px'
                                                }}>
                                                    <strong style={{ color: '#1976d2', display: 'block', marginBottom: '10px', fontSize: '14px' }}>
                                                        📋 Creative Requirements for {formData.addDisplayPosition.replace(/_/g, ' ')}:
                                                    </strong>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 1fr',
                                                        gap: '10px'
                                                    }}>
                                                        <div>📁 <strong>Formats:</strong> {getCreativeRequirements(formData.addDisplayPosition).formats.join(', ')}</div>
                                                        <div>📐 <strong>Dimensions:</strong> {getCreativeRequirements(formData.addDisplayPosition).dimensions}</div>
                                                        <div>⚖️ <strong>Max Size:</strong> {getCreativeRequirements(formData.addDisplayPosition).maxSize}</div>
                                                        <div>🖼️ <strong>Aspect Ratio:</strong> {getCreativeRequirements(formData.addDisplayPosition).aspectRatio}</div>
                                                        <div>⏱️ <strong>Video Duration:</strong> {getCreativeRequirements(formData.addDisplayPosition).duration}</div>
                                                        <div>🎯 <strong>Best Ratio:</strong> {getCreativeRequirements(formData.addDisplayPosition).bestRatio}</div>
                                                    </div>
                                                    <div style={{
                                                        marginTop: '15px',
                                                        padding: '10px',
                                                        backgroundColor: '#c8e6c9',
                                                        borderRadius: '4px',
                                                        color: '#2e7d32',
                                                        fontSize: '12px'
                                                    }}>
                                                        <strong>✨ Automatic Optimization:</strong> Your file will be automatically compressed by 30% to ensure optimal delivery and faster loading times.
                                                    </div>
                                                </div>
                                            )}

                                            <small style={{ color: '#666', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                                                * Please ensure your creative meets the requirements above. Files will be automatically compressed by 30% for optimal performance.
                                            </small>
                                        </div>

                                        {/* Description */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Description</label>
                                            <textarea
                                                name="description"
                                                placeholder="Enter campaign description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows="3"
                                                style={{ ...inputStyle, resize: 'vertical' }}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* DIGITAL-Specific Fields */}
                                {formData.medium === "DIGITAL" && (
                                    <>
                                        {/* Start Date */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Start Date *</label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={formData.startDate}
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* End Date */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>End Date *</label>
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={formData.endDate}
                                                onChange={handleChange}
                                                min={formData.startDate || new Date().toISOString().split('T')[0]}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* City */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>City</label>
                                            <input
                                                type="text"
                                                name="city"
                                                placeholder="Enter city name"
                                                value={formData.city}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </div>


                                        {/* Media Type */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Media Type *</label>
                                            <select
                                                name="mediaType"
                                                value={formData.mediaType}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            >
                                                {mediaTypeOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Creative File */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Upload File *</label>
                                            <input
                                                type="file"
                                                name="creative"
                                                onChange={handleCreativeChange}
                                                required
                                                style={{
                                                    ...inputStyle,
                                                    padding: '8px',
                                                    cursor: 'pointer',
                                                    borderColor: creativeFile ? (isCreativeValidForScreens() ? '#4CAF50' : '#f44336') : '#ddd'
                                                }}
                                                accept=".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.mkv,.webm"
                                            />

                                            {/* Show creative validation status */}
                                            {creativeFile && (
                                                <div style={{
                                                    marginTop: '10px',
                                                    padding: '10px',
                                                    backgroundColor: isCreativeValidForScreens() ? '#e8f5e8' : '#ffebee',
                                                    borderRadius: '4px',
                                                    border: `1px solid ${isCreativeValidForScreens() ? '#4CAF50' : '#f44336'}`
                                                }}>
                                                    <strong style={{ color: isCreativeValidForScreens() ? '#2e7d32' : '#c62828' }}>
                                                        {isCreativeValidForScreens() ? '✓ Creative Valid' : '✗ Creative Validation Failed'}
                                                    </strong>
                                                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                                                        {creativeValidationMessages.map((msg, idx) => (
                                                            <li key={idx} style={{ color: msg.valid ? '#2e7d32' : '#c62828' }}>
                                                                {msg.message}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Creative Requirements Box */}
                                            <div style={{
                                                marginTop: '15px',
                                                padding: '15px',
                                                backgroundColor: '#e3f2fd',
                                                borderLeft: '4px solid #2196f3',
                                                borderRadius: '4px',
                                                fontSize: '13px'
                                            }}>
                                                <strong style={{ color: '#1976d2', display: 'block', marginBottom: '10px', fontSize: '14px' }}>
                                                    📋 Creative Requirements for Digital Screens:
                                                </strong>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    gap: '10px'
                                                }}>
                                                    <div>📁 <strong>Formats:</strong> JPG, PNG, MP4</div>
                                                    <div>📐 <strong>Dimensions:</strong> 1920x1080 pixels (16:9)</div>
                                                    <div>⚖️ <strong>Max Size:</strong> 5 MB for images, 10 MB for videos</div>
                                                    <div>⏱️ <strong>Video Duration:</strong> Maximum 20 seconds</div>
                                                </div>
                                                <div style={{
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    backgroundColor: '#c8e6c9',
                                                    borderRadius: '4px',
                                                    color: '#2e7d32',
                                                    fontSize: '12px'
                                                }}>
                                                    <strong>✨ Automatic Optimization:</strong> Your file will be automatically compressed by 30% to ensure optimal delivery and faster loading times.
                                                </div>
                                            </div>

                                            <small style={{ color: '#666', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                                                * Please ensure your creative meets the requirements above. Only screens with compatible resolution will be available for selection.
                                            </small>
                                        </div>

                                        {/* Screen Selection with Checkboxes */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Select Screens *</label>

                                            {loadingScreens ? (
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        alignItems: "center",
                                                        padding: "20px",
                                                        gap: "10px",
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: "20px",
                                                            height: "20px",
                                                            border: "2px solid #f3f3f3",
                                                            borderTop: "2px solid rgb(230 109 51)",
                                                            borderRadius: "50%",
                                                            animation: "spin 1s linear infinite",
                                                        }}
                                                    ></div>
                                                    <span>Loading screens...</span>
                                                </div>
                                            ) : screens.length > 0 ? (
                                                <>
                                                    <div
                                                        style={{
                                                            border: "1px solid #ddd",
                                                            borderRadius: "6px",
                                                            padding: "15px",
                                                            maxHeight: "300px",
                                                            overflowY: "auto",
                                                            backgroundColor: "#fff",
                                                        }}
                                                    >
                                                        {screens.map((screen) => {
                                                            const screenId = screen.id || screen.screenId;
                                                            const screenName =
                                                                screen.name || screen.screenName || `Screen ${screenId}`;
                                                            const location = screen.location || screen.city || "Unknown location";
                                                            const screenResolution =
                                                                screen.resolution || screen.aspectRatio || "16:9";

                                                            return (
                                                                <label
                                                                    key={screenId}
                                                                    style={{
                                                                        display: "flex",
                                                                        alignItems: "flex-start",
                                                                        marginBottom: "12px",
                                                                        cursor: "pointer",
                                                                        padding: "10px",
                                                                        borderRadius: "4px",
                                                                        backgroundColor: screenIds.includes(screenId)
                                                                            ? "#e3f2fd"
                                                                            : "transparent",
                                                                        border: "1px solid #e0e0e0",
                                                                    }}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={screenIds.includes(screenId)}
                                                                        onChange={() => handleScreenSelection(screenId)}
                                                                        style={{
                                                                            marginRight: "12px",
                                                                            marginTop: "2px",
                                                                            cursor: "pointer",
                                                                            width: "16px",
                                                                            height: "16px",
                                                                        }}
                                                                    />

                                                                    <div style={{ flex: 1 }}>
                                                                        <div
                                                                            style={{
                                                                                fontSize: "14px",
                                                                                fontWeight: "600",
                                                                                marginBottom: "4px",
                                                                            }}
                                                                        >
                                                                            {screenName}
                                                                        </div>

                                                                        <div
                                                                            style={{
                                                                                fontSize: "12px",
                                                                                color: "#666",
                                                                                display: "flex",
                                                                                flexWrap: "wrap",
                                                                                gap: "8px",
                                                                            }}
                                                                        >
                                                                            <span>ID: {screenId}</span>
                                                                            <span>📍 {location}</span>
                                                                            <span>📺 Resolution: {screenResolution}</span>
                                                                        </div>
                                                                    </div>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>

                                                    <small
                                                        style={{
                                                            color: "#666",
                                                            fontSize: "12px",
                                                            marginTop: "8px",
                                                            display: "block",
                                                        }}
                                                    >
                                                        Selected screens: {screenIds.length}{" "}
                                                        {screenIds.length === 1 ? "screen" : "screens"}
                                                    </small>
                                                </>
                                            ) : (
                                                <div
                                                    style={{
                                                        padding: "20px",
                                                        textAlign: "center",
                                                        backgroundColor: "#f5f5f5",
                                                        borderRadius: "6px",
                                                        border: "1px dashed #ccc",
                                                    }}
                                                >
                                                    <p style={{ color: "#666", marginBottom: "10px" }}>
                                                        No screens available. Please add screens first.
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Description */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Description</label>
                                            <textarea
                                                name="description"
                                                placeholder="Enter campaign description"
                                                value={formData.description}
                                                onChange={handleChange}
                                                rows="3"
                                                style={{ ...inputStyle, resize: 'vertical' }}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Form Buttons */}
                                <div style={{
                                    display: 'flex',
                                    gap: '10px',
                                    marginTop: '20px',
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: '#fff',
                                    padding: '10px 0',
                                    borderTop: '1px solid #eee'
                                }}>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px',
                                            backgroundColor: '#1976d2',
                                            color: 'white',
                                            fontWeight: '600',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            flex: 1,
                                            fontSize: '14px',
                                        }}
                                        onMouseOver={e => e.target.style.backgroundColor = '#1565c0'}
                                        onMouseOut={e => e.target.style.backgroundColor = '#1976d2'}
                                    >
                                        Create Campaign
                                    </button>
                                    <button
                                        type="button"
                                        style={{
                                            padding: '12px',
                                            backgroundColor: '#f5f5f5',
                                            color: '#333',
                                            fontWeight: '600',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            flex: 1,
                                            fontSize: '14px',
                                        }}
                                        onClick={() => {
                                            setShowAddModal(false);
                                            resetForm();
                                        }}
                                        onMouseOver={e => e.target.style.backgroundColor = '#e0e0e0'}
                                        onMouseOut={e => e.target.style.backgroundColor = '#f5f5f5'}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Campaign Modal */}
            {showEditModal && selectedCampaign && (
                <div style={modalOverlayStyle} onClick={(e) => {
                    if (e.target === e.currentTarget) {
                        setShowEditModal(false);
                        setSelectedCampaign(null);
                        resetForm();
                    }
                }}>
                    <div style={modalContentStyle} className="modal-enter">
                        <div style={modalHeaderStyle}>
                            <h3 style={{
                                fontSize: isMobile ? '16px' : '18px',
                                fontWeight: 'bold',
                                margin: 0,
                                color: '#333'
                            }}>
                                Edit Campaign
                            </h3>
                            <button
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    fontWeight: 'bold',
                                    padding: '0',
                                    width: '30px',
                                    height: '30px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '4px',
                                }}
                                onClick={() => {
                                    setShowEditModal(false);
                                    setSelectedCampaign(null);
                                    resetForm();
                                }}
                                onMouseOver={e => e.target.style.backgroundColor = '#f0f0f0'}
                                onMouseOut={e => e.target.style.backgroundColor = 'transparent'}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={modalBodyStyle}>
                            <form onSubmit={handleEditCampaign}>
                                {/* Campaign Name */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Promotion Name *</label>
                                    <input
                                        type="text"
                                        name="campaignName"
                                        placeholder="Enter Promotion name"
                                        value={formData.campaignName}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    />
                                </div>

                                {/* Customer ID Field */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Customer ID *</label>
                                    <input
                                        type="text"
                                        name="customerId"
                                        placeholder="Enter customer ID"
                                        value={formData.customerId}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    />
                                </div>

                                {/* Description */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Description</label>
                                    <textarea
                                        name="description"
                                        placeholder="Enter campaign description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="3"
                                        style={{ ...inputStyle, resize: 'vertical' }}
                                    />
                                </div>

                                {/* Goal */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Goal *</label>
                                    <select
                                        name="goal"
                                        value={formData.goal}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    >
                                        {goalOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Medium */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Medium *</label>
                                    <select
                                        name="medium"
                                        value={formData.medium}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    >
                                        {mediumOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Payment Status */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Payment Status *</label>
                                    <select
                                        name="paymentStatus"
                                        value={formData.paymentStatus}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    >
                                        {paymentStatusOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* APP-Specific Fields */}
                                {formData.medium === "APP" && (
                                    <>
                                        {/* Start Date */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Start Date *</label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={formData.startDate}
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* End Date */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>End Date *</label>
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={formData.endDate}
                                                onChange={handleChange}
                                                min={formData.startDate || new Date().toISOString().split('T')[0]}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* Interests (Checkboxes) */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Interests *</label>
                                            <div style={{
                                                border: '1px solid #ddd',
                                                borderRadius: '6px',
                                                padding: '12px',
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                backgroundColor: '#fff'
                                            }}>
                                                {interestOptions.map((option) => (
                                                    <label
                                                        key={option}
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            marginBottom: '8px',
                                                            cursor: 'pointer',
                                                            padding: '4px',
                                                            borderRadius: '4px',
                                                            backgroundColor: formData.interests.includes(option) ? '#e3f2fd' : 'transparent',
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            value={option}
                                                            checked={formData.interests.includes(option)}
                                                            onChange={(e) => {
                                                                const { value, checked } = e.target;
                                                                let updatedInterests = [...formData.interests];

                                                                if (checked) {
                                                                    updatedInterests.push(value);
                                                                } else {
                                                                    updatedInterests = updatedInterests.filter(interest => interest !== value);
                                                                }

                                                                if (updatedInterests.length === 0) {
                                                                    alert("Please select at least one interest");
                                                                    return;
                                                                }

                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    interests: updatedInterests,
                                                                    targeting: {
                                                                        ...prev.targeting,
                                                                        interests: updatedInterests
                                                                    }
                                                                }));
                                                            }}
                                                            style={{
                                                                marginRight: '10px',
                                                                cursor: 'pointer',
                                                                width: '16px',
                                                                height: '16px'
                                                            }}
                                                        />
                                                        <span style={{
                                                            fontSize: '14px',
                                                            fontWeight: formData.interests.includes(option) ? '600' : '400'
                                                        }}>
                                                            {option}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                                Selected: {formData.interests.join(', ')}
                                            </small>
                                        </div>

                                        {/* City with search */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>City</label>
                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                <input
                                                    type="text"
                                                    name="city"
                                                    placeholder="Enter city name"
                                                    value={formData.city}
                                                    onChange={handleChange}
                                                    style={{ ...inputStyle, flex: 1 }}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => goToCity(formData.city)}
                                                    style={{
                                                        padding: '10px 20px',
                                                        backgroundColor: '#4CAF50',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '6px',
                                                        cursor: 'pointer',
                                                        fontWeight: '500',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    Search
                                                </button>
                                            </div>
                                        </div>

                                        {/* Location Map */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <label style={labelStyle}>Location</label>
                                                <button
                                                    type="button"
                                                    onClick={getCurrentLocation}
                                                    style={{
                                                        padding: '6px 12px',
                                                        backgroundColor: '#2196F3',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px'
                                                    }}
                                                >
                                                    Get Current Location
                                                </button>
                                            </div>
                                            <div style={{ position: 'relative', height: '300px' }}>
                                                <div
                                                    id="edit-map"
                                                    style={{
                                                        width: '100%',
                                                        height: '100%',
                                                        borderRadius: '8px',
                                                        border: '1px solid #ddd'
                                                    }}
                                                ></div>
                                                {!locationLoaded && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        backgroundColor: 'rgba(0,0,0,0.3)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        borderRadius: '8px'
                                                    }}>
                                                        <div style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            border: '3px solid #f3f3f3',
                                                            borderTop: '3px solid #FF5722',
                                                            borderRadius: '50%',
                                                            animation: 'spin 1s linear infinite'
                                                        }}></div>
                                                    </div>
                                                )}
                                            </div>
                                            <input
                                                ref={searchInputRef}
                                                type="text"
                                                placeholder="Search for a location"
                                                style={{
                                                    ...inputStyle,
                                                    marginTop: '10px'
                                                }}
                                            />
                                        </div>

                                        {/* Target Radius with Slider */}
                                        <div style={{ marginBottom: '20px', backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '8px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                                <label style={labelStyle}>Target Radius</label>
                                                <span style={{
                                                    padding: '4px 12px',
                                                    backgroundColor: '#FF5722',
                                                    color: 'white',
                                                    borderRadius: '20px',
                                                    fontWeight: 'bold',
                                                    fontSize: '14px'
                                                }}>
                                                    {formData.radiusKm} km
                                                </span>
                                            </div>
                                            <input
                                                type="range"
                                                name="radiusKm"
                                                min="0"
                                                max="100"
                                                step="1"
                                                value={formData.radiusKm}
                                                onChange={handleChange}
                                                style={{ width: '100%', accentColor: '#FF5722' }}
                                            />
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px', color: '#666', fontSize: '12px' }}>
                                                <span>0 km</span>
                                                <span>25 km</span>
                                                <span>50 km</span>
                                                <span>100 km</span>
                                            </div>
                                        </div>

                                        {/* Deep Link */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Deep Link</label>
                                            <input
                                                type="url"
                                                name="deepLink"
                                                placeholder="https://example.com"
                                                value={formData.deepLink}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* App Type */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>App Type *</label>
                                            <select
                                                name="appType"
                                                value={formData.appType}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            >
                                                {appTypeOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option.replace(/_/g, ' ')}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Media Type */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Media Type *</label>
                                            <select
                                                name="mediaType"
                                                value={formData.mediaType}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            >
                                                {mediaTypeOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                                Select the type of media you're uploading
                                            </small>
                                        </div>

                                        {/* Add Display Position */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Display Position *</label>
                                            <select
                                                name="addDisplayPosition"
                                                value={formData.addDisplayPosition}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            >
                                                {displayPositionOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option.replace(/_/g, ' ')}
                                                    </option>
                                                ))}
                                            </select>

                                            {/* Display Position Info Table */}
                                            {formData.addDisplayPosition && (
                                                <div style={{
                                                    marginTop: '15px',
                                                    padding: '15px',
                                                    backgroundColor: '#f0f7ff',
                                                    borderRadius: '8px',
                                                    border: '1px solid #b8daff',
                                                }}>
                                                    <h4 style={{
                                                        margin: '0 0 10px 0',
                                                        fontSize: '14px',
                                                        color: '#004085',
                                                        fontWeight: '600'
                                                    }}>
                                                        📊 Recommended Specifications for {formData.addDisplayPosition.replace(/_/g, ' ')}:
                                                    </h4>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 1fr',
                                                        gap: '10px',
                                                        fontSize: '13px'
                                                    }}>
                                                        <div><strong>Best Ratio:</strong> {displayPositionRatios[formData.addDisplayPosition]?.ratio}</div>
                                                        <div><strong>Auto Resolution:</strong> {displayPositionRatios[formData.addDisplayPosition]?.resolution}</div>
                                                        <div><strong>Description:</strong> {displayPositionRatios[formData.addDisplayPosition]?.description}</div>
                                                        <div><strong>Media Type:</strong> {formData.mediaType}</div>
                                                    </div>
                                                    <p style={{
                                                        margin: '10px 0 0 0',
                                                        fontSize: '12px',
                                                        color: '#28a745',
                                                        fontStyle: 'italic'
                                                    }}>
                                                        ⚡ Resolution will be auto-set based on display position
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* DIGITAL-Specific Fields */}
                                {formData.medium === "DIGITAL" && (
                                    <>
                                        {/* Start Date */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Start Date *</label>
                                            <input
                                                type="date"
                                                name="startDate"
                                                value={formData.startDate}
                                                min={new Date().toISOString().split('T')[0]}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* End Date */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>End Date *</label>
                                            <input
                                                type="date"
                                                name="endDate"
                                                value={formData.endDate}
                                                onChange={handleChange}
                                                min={formData.startDate || new Date().toISOString().split('T')[0]}
                                                required
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* City */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>City</label>
                                            <input
                                                type="text"
                                                name="city"
                                                placeholder="Enter city name"
                                                value={formData.city}
                                                onChange={handleChange}
                                                style={inputStyle}
                                            />
                                        </div>

                                        {/* Media Type */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Media Type *</label>
                                            <select
                                                name="mediaType"
                                                value={formData.mediaType}
                                                onChange={handleChange}
                                                required
                                                style={inputStyle}
                                            >
                                                {mediaTypeOptions.map((option) => (
                                                    <option key={option} value={option}>
                                                        {option}
                                                    </option>
                                                ))}
                                            </select>
                                            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                                                Select the type of media you're uploading
                                            </small>
                                        </div>

                                        {/* Creative File - Before screen selection */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Creative File</label>
                                            <input
                                                type="file"
                                                name="creative"
                                                onChange={handleCreativeChange}
                                                style={{
                                                    ...inputStyle,
                                                    padding: '8px',
                                                    cursor: 'pointer',
                                                    borderColor: creativeFile ? (isCreativeValidForScreens() ? '#4CAF50' : '#f44336') : '#ddd'
                                                }}
                                                accept=".jpg,.jpeg,.png,.gif,.mp4,.mov,.avi,.mkv,.webm"
                                            />

                                            {/* Show creative validation status */}
                                            {creativeFile && (
                                                <div style={{
                                                    marginTop: '10px',
                                                    padding: '10px',
                                                    backgroundColor: isCreativeValidForScreens() ? '#e8f5e8' : '#ffebee',
                                                    borderRadius: '4px',
                                                    border: `1px solid ${isCreativeValidForScreens() ? '#4CAF50' : '#f44336'}`
                                                }}>
                                                    <strong style={{ color: isCreativeValidForScreens() ? '#2e7d32' : '#c62828' }}>
                                                        {isCreativeValidForScreens() ? '✓ Creative Valid' : '✗ Creative Validation Failed'}
                                                    </strong>
                                                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px' }}>
                                                        {creativeValidationMessages.map((msg, idx) => (
                                                            <li key={idx} style={{ color: msg.valid ? '#2e7d32' : '#c62828' }}>
                                                                {msg.message}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}

                                            {/* Creative Requirements Box */}
                                            <div style={{
                                                marginTop: '15px',
                                                padding: '15px',
                                                backgroundColor: '#fff3e0',
                                                borderLeft: '4px solid #ff9800',
                                                borderRadius: '4px',
                                                fontSize: '13px'
                                            }}>
                                                <strong style={{ color: '#ed6c02', display: 'block', marginBottom: '10px', fontSize: '14px' }}>
                                                    📋 Creative Requirements for Digital Screens:
                                                </strong>
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    gap: '10px'
                                                }}>
                                                    <div>📁 <strong>Formats:</strong> JPG, PNG, MP4</div>
                                                    <div>📐 <strong>Dimensions:</strong> 1920x1080 pixels (16:9)</div>
                                                    <div>⚖️ <strong>Max Size:</strong> 5 MB for images, 10 MB for videos</div>
                                                    <div>⏱️ <strong>Video Duration:</strong> Maximum 20 seconds</div>
                                                </div>
                                                <div style={{
                                                    marginTop: '15px',
                                                    padding: '10px',
                                                    backgroundColor: '#c8e6c9',
                                                    borderRadius: '4px',
                                                    color: '#2e7d32',
                                                    fontSize: '12px'
                                                }}>
                                                    <strong>✨ Automatic Optimization:</strong> Your file will be automatically compressed by 30% to ensure optimal delivery and faster loading times.
                                                </div>
                                            </div>

                                            <small style={{ color: '#666', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                                                * Leave empty to keep existing creative. New upload must meet requirements above. Only screens with compatible resolution will be available for selection.
                                            </small>
                                        </div>

                                        {/* Screen Selection with Checkboxes */}
                                        <div style={{ marginBottom: '20px' }}>
                                            <label style={labelStyle}>Select Screens *</label>

                                            {loadingScreens ? (
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    padding: '20px',
                                                    gap: '10px'
                                                }}>
                                                    <div style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        border: '2px solid #f3f3f3',
                                                        borderTop: '2px solid rgb(230 109 51)',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite'
                                                    }}></div>
                                                    <span>Loading screens...</span>
                                                </div>
                                            ) : screens.length > 0 ? (
                                                <>
                                                    {/* Filter screens based on creative compatibility */}
                                                    {getCompatibleScreens().length === 0 && creativeFile ? (
                                                        <div style={{
                                                            padding: '20px',
                                                            textAlign: 'center',
                                                            backgroundColor: '#ffebee',
                                                            borderRadius: '6px',
                                                            border: '1px solid #f44336'
                                                        }}>
                                                            <p style={{ color: '#c62828', marginBottom: '10px', fontWeight: 'bold' }}>
                                                                No compatible screens found!
                                                            </p>
                                                            <p style={{ color: '#666', fontSize: '13px' }}>
                                                                Your creative's resolution ({getCreativeResolution()}) doesn't match any available screens.
                                                                Please upload a creative with 16:9 aspect ratio (1920x1080 pixels).
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        <div style={{
                                                            border: '1px solid #ddd',
                                                            borderRadius: '6px',
                                                            padding: '15px',
                                                            maxHeight: '300px',
                                                            overflowY: 'auto',
                                                            backgroundColor: '#fff'
                                                        }}>
                                                            {/* Individual Screens */}
                                                            {screens.map((screen) => {
                                                                const screenId = screen.id || screen.screenId;
                                                                const screenName = screen.name || screen.screenName || `Screen ${screenId}`;
                                                                const location = screen.location || screen.city || 'Unknown location';
                                                                const screenResolution = screen.resolution || screen.aspectRatio || '16:9';
                                                                const isCompatible = isScreenCompatible(screen);

                                                                return (
                                                                    <label
                                                                        key={screenId}
                                                                        style={{
                                                                            display: 'flex',
                                                                            alignItems: 'flex-start',
                                                                            marginBottom: '12px',
                                                                            cursor: creativeFile && isCompatible ? 'pointer' : 'not-allowed',
                                                                            padding: '10px',
                                                                            borderRadius: '4px',
                                                                            backgroundColor: screenIds.includes(screenId) ? '#e3f2fd' : 'transparent',
                                                                            border: `1px solid ${isCompatible ? '#e0e0e0' : '#ffcdd2'}`,
                                                                            opacity: creativeFile && isCompatible ? 1 : 0.5
                                                                        }}
                                                                    >
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={screenIds.includes(screenId)}
                                                                            onChange={() => handleScreenSelection(screenId)}
                                                                            disabled={!creativeFile || !isCompatible}
                                                                            style={{
                                                                                marginRight: '12px',
                                                                                marginTop: '2px',
                                                                                cursor: creativeFile && isCompatible ? 'pointer' : 'not-allowed',
                                                                                width: '16px',
                                                                                height: '16px'
                                                                            }}
                                                                        />
                                                                        <div style={{ flex: 1 }}>
                                                                            <div style={{
                                                                                fontSize: '14px',
                                                                                fontWeight: '600',
                                                                                marginBottom: '4px',
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between'
                                                                            }}>
                                                                                <span>{screenName}</span>
                                                                                {!isCompatible && creativeFile && (
                                                                                    <span style={{
                                                                                        fontSize: '11px',
                                                                                        color: '#f44336',
                                                                                        backgroundColor: '#ffebee',
                                                                                        padding: '2px 6px',
                                                                                        borderRadius: '3px'
                                                                                    }}>
                                                                                        Incompatible resolution
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            <div style={{
                                                                                fontSize: '12px',
                                                                                color: '#666',
                                                                                display: 'flex',
                                                                                flexWrap: 'wrap',
                                                                                gap: '8px'
                                                                            }}>
                                                                                <span>ID: {screenId}</span>
                                                                                <span>📍 {location}</span>
                                                                                <span>📺 Resolution: {screenResolution}</span>
                                                                                {screen.resolution && (
                                                                                    <span>📐 {screen.resolution}</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </label>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    <small style={{ color: '#666', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                                                        Selected screens: {screenIds.length} {screenIds.length === 1 ? 'screen' : 'screens'}
                                                        {!creativeFile && ' (Upload creative first to enable screen selection)'}
                                                    </small>
                                                </>
                                            ) : (
                                                <div style={{
                                                    padding: '20px',
                                                    textAlign: 'center',
                                                    backgroundColor: '#f5f5f5',
                                                    borderRadius: '6px',
                                                    border: '1px dashed #ccc'
                                                }}>
                                                    <p style={{ color: '#666', marginBottom: '10px' }}>
                                                        No screens available. Please add screens first.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Status */}
                                <div style={{ marginBottom: '20px' }}>
                                    <label style={labelStyle}>Status *</label>
                                    <select
                                        name="status"
                                        value={formData.status}
                                        onChange={handleChange}
                                        required
                                        style={inputStyle}
                                    >
                                        {statusOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Form Buttons */}
                                <div style={{
                                    display: 'flex',
                                    gap: '10px',
                                    marginTop: '20px',
                                    position: 'sticky',
                                    bottom: 0,
                                    backgroundColor: '#fff',
                                    padding: '10px 0',
                                    borderTop: '1px solid #eee'
                                }}>
                                    <button
                                        type="submit"
                                        style={{
                                            padding: '12px',
                                            backgroundColor: '#2196F3',
                                            color: 'white',
                                            fontWeight: '600',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            flex: 1,
                                            fontSize: '14px',
                                        }}
                                        onMouseOver={e => e.target.style.backgroundColor = '#1976d2'}
                                        onMouseOut={e => e.target.style.backgroundColor = '#2196F3'}
                                    >
                                        Update Campaign
                                    </button>
                                    <button
                                        type="button"
                                        style={{
                                            padding: '12px',
                                            backgroundColor: '#f5f5f5',
                                            color: '#333',
                                            fontWeight: '600',
                                            border: '1px solid #ddd',
                                            borderRadius: '6px',
                                            cursor: 'pointer',
                                            flex: 1,
                                            fontSize: '14px',
                                        }}
                                        onClick={() => {
                                            setShowEditModal(false);
                                            setSelectedCampaign(null);
                                            resetForm();
                                        }}
                                        onMouseOver={e => e.target.style.backgroundColor = '#e0e0e0'}
                                        onMouseOut={e => e.target.style.backgroundColor = '#f5f5f5'}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Reusable styles
const inputStyle = {
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '6px',
    width: '100%',
    boxSizing: 'border-box',
    fontSize: '14px',
    transition: 'border-color 0.3s',
    backgroundColor: '#fff',
};

const labelStyle = {
    fontSize: '14px',
    marginBottom: '6px',
    display: 'block',
    fontWeight: '500',
    color: '#333'
};

export default FoodSuperadminCampaign;